import { SwitcherViewModel } from "./switcher.view-model";
import { SwitcherItemViewModel } from "./switcher-item.view-model";
import { ESwitcherAssetType } from "../../../domain/broadcasts/switcher-item.type.enum";
import { UI } from "../../../services/ui.service";
import { BroadcastPreset } from "../../../domain/broadcasts/broadcast-preset";
import { Switcher } from "../../../domain/broadcasts/switcher";
import { SwitcherItem } from "../../../domain/broadcasts/switcher-item";
import {
  ELiveBroadcastingAction,
  IBroadcastsRepository,
} from "../../../domain/broadcasts/i-broadcasts-repository";
import { NavigationEnd, Router } from "@angular/router";
import { filter, Subscription, debounceTime, Subject } from "rxjs";
import { ICommonUtilInterface } from "../../../domain/common/common-util.interface";
import { Socket } from "../../../infrastructure/socket.service";
import { WebRTCPreview } from "../../../domain/web-r-t-c-preview";
import { ChangeDetectorRef } from "@angular/core";
import { MediaItem } from "../../../domain/media";
import { LiveFeedInfo } from "../../../domain/live-feed";
import { GraphicsInfo } from "../../../domain/graphics-info";
import { Layout } from "../../../domain/layouts/layout";
import { Block } from "../../../domain/blocks/block";
import { UserService } from "../../../domain/user-service";
import { Channel, ChannelInfo } from "../../../domain/channel";
import {
  BroadcastModeUpdate,
  EChannelBroadcastMode,
  IChannelBroadcastModeChecker,
} from "../../../domain/broadcasts/i-channel-broadcast-mode-checker";
import moment from "moment/moment";
import { ChannelRepository } from "../../../domain/channel-repository";
import { ShowRepository } from "../../../domain/show-repository";
import { CalendarService } from "../../../domain/calendar-service";
import {
  BroadcastPresetShow,
  LayoutShow,
  LiveShow,
  MediaShow,
  Show,
} from "../../../domain/show";
import { ILayoutsRepository } from "../../../domain/layouts/layouts.repository";
import { ELayoutZoneType } from "../../../domain/layouts/layoutZone";
import { IBlockRepository } from "app/js/src/domain/blocks/i-block.repository";
import {
  assetBlockItemExtendedData,
  broadcastAssetItemExtendedData,
} from "app/js/src/domain/broadcasts/asset-block-item-extended-data";
import { BroadcastPresetAudioChannel } from "app/js/src/domain/broadcasts/broadcast-preset-audio-channel";

/** SCTE-35 switcher type (value after Stack). Enum member may be missing after merge; keep numeric parity. */
const SWITCHER_ASSET_TYPE_SCTE35 = 7 as ESwitcherAssetType;

export class BroadcastPresetViewModel extends BroadcastPreset {
  public selectedSwitcher: SwitcherViewModel;

  public previewWindowData: WebRTCPreviewViewModel;
  public programWindowData: WebRTCPreviewViewModel;

  public lastCommandResponse = "";
  public lastCommandName = "";
  public lastCommandProcessingTimeMs = 0;
  public debugMode = false;
  public allowLiveMode = false;
  public forceShowingCutButton = false;

  public scte35TagActive = false;
  private scte35TagTimer: ReturnType<typeof setTimeout> | null = null;

  public editMode = true;
  public mode: EChannelBroadcastMode = EChannelBroadcastMode.Off;

  public channelMediaListChanged: Subject<boolean> = new Subject<boolean>();

  private _currentSelectedChannel: Channel;
  private _skipToProgramOnStartLiveClicked = false;
  private _onBroadcastDeletedSubscription: Subscription;
  private _onSwitchItemBlockTypeChangedSubscription: Subscription;
  private _allowedAssetIdPerTypeLimit: Map<ESwitcherAssetType, number>;
  private _allowedAssetsPerTypeLimit: Map<ESwitcherAssetType, number>;
  private _routerEndSubscription: Subscription;
  private _duringDeleteSwitcher: boolean;
  private _channelBroadcastModeChangedSubscription: Subscription;
  private _onHubReconnectedSubscription: Subscription;

  public channelsMap: Map<string, string> = new Map<string, string>();
  creatingProgramWindow: boolean;
  private duringModeUpdate: boolean;
  private _currentSelectedChannelInfo: ChannelInfo;

  public get switchersVMs(): SwitcherViewModel[] {
    return this.switchers as SwitcherViewModel[];
  }

  public get addSwitchersEnabled(): boolean {
    return this.switchersVMs && this.switchersVMs.length < 7;
  }
  public get removeSwitchersEnabled(): boolean {
    return this.switchersVMs && this.switchersVMs.length > 1;
  }

  private _intervalsPerChannelUid = {};
  private _channelBroadcastUpdatesDebounceTimeouts: {
    [channelUid: string]: any;
  } = {};
  private _previewTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private _startLiveRefreshTimeoutId: ReturnType<typeof setTimeout> | null =
    null;
  private _videoReconnectTimeoutIds: ReturnType<typeof setTimeout>[] = [];

  constructor(
    broadcastPreset: BroadcastPreset,
    private _ui: UI,
    private _broadcastsRepository: IBroadcastsRepository,
    private _commonUtils: ICommonUtilInterface,
    private _router: Router,
    private _socketService: Socket,
    private _changeDetectorRef: ChangeDetectorRef,
    private _userService: UserService,
    private _channelBroadcastModeChecker: IChannelBroadcastModeChecker,
    private _channelRepository: ChannelRepository,
    private _showRepository: ShowRepository,
    private _calendarService: CalendarService,
    private _layoutRepository: ILayoutsRepository,
    private _blocksRepository: IBlockRepository,
  ) {
    super(
      broadcastPreset.id,
      broadcastPreset.name,
      broadcastPreset.thumbnailUrl,
      broadcastPreset.switchers.map(
        (s) =>
          new SwitcherViewModel(
            s,
            _changeDetectorRef,
            _ui,
            _broadcastsRepository,
            _blocksRepository,
          ),
      ),
      broadcastPreset.audioChannels,
    );

    this.selectedSwitcher = this.switchersVMs[0];
  }
  public addSwitcherClicked() {
    if (this.addSwitchersEnabled) {
      this.addNewSwitcher();
    }
  }
  public async onInit() {
    this._onBroadcastDeletedSubscription =
      this._broadcastsRepository.deleted.subscribe((deletedBroadcastPreset) => {
        if (deletedBroadcastPreset.id === this.id) {
          this._router.navigate(["/scheduler/calendar"]).then();
        }
      });
    this._channelBroadcastModeChangedSubscription =
      this._channelBroadcastModeChecker.modeUpdated
        .pipe(debounceTime(1000))
        .pipe(debounceTime(1000))
        .subscribe(this._channelLiveBroadcastingModeChanged.bind(this));

    this._currentSelectedChannel = await this._userService.getCurrentChannel();
    this._currentSelectedChannelInfo =
      await this._userService.getCurrentChannelInfo();
    this._allowedAssetIdPerTypeLimit = new Map<ESwitcherAssetType, number>();
    //parsing broadcast items limits
    this._userService
      .getCurrentUser()
      .broadcastAssetIdPerTypeLimits?.forEach((limitTypeNumber, index) => {
        this._allowedAssetIdPerTypeLimit.set(index + 1, limitTypeNumber);
      });
    this._allowedAssetsPerTypeLimit = new Map<ESwitcherAssetType, number>();
    this._userService
      .getCurrentUser()
      .broadcastAssetsPerTypeLimits?.forEach((limitTypeNumber, index) => {
        this._allowedAssetsPerTypeLimit.set(index + 1, limitTypeNumber);
      });

    // First: Create PROGRAM window
    const createWebrtcForProgramResult =
      await this._createWebRTCPreviewChannelForBroadcast(
        true,
        this._currentSelectedChannel.id,
      );
    // tslint:disable-next-line:max-line-length
    this.programWindowData = this.createWebRTCPreviewViewModel(
      true,
      createWebrtcForProgramResult.clientId,
      createWebrtcForProgramResult.edgeBrodcasterId,
      [createWebrtcForProgramResult.url],
      true,
      this.debugMode,
      createWebrtcForProgramResult.channelUid,
      [createWebrtcForProgramResult.availabilityZoneId],
      "",
    );

    // Then: Create PREVIEW window
    const createWebrtcForPreviewResult =
      await this._createWebRTCPreviewChannelForBroadcast(
        false,
        this._currentSelectedChannel.id,
      );
    // tslint:disable-next-line:max-line-length
    this.previewWindowData = this.createWebRTCPreviewViewModel(
      false,
      createWebrtcForPreviewResult.clientId,
      createWebrtcForPreviewResult.edgeBrodcasterId,
      [createWebrtcForPreviewResult.url],
      true,
      this.debugMode,
      createWebrtcForPreviewResult.channelUid,
      [createWebrtcForPreviewResult.availabilityZoneId],
      "",
    );

    // Subscribe to stack/channel broadcast updates immediately so we receive
    // them without waiting for triggerModeUpdate (~1.5s+). We call again after
    // triggerModeUpdate so reconnection and mode flow still re-subscribe.
    await this._resubscribeToStackAndChannelBroadcastUpdates();

    // Add a small delay to prevent race conditions
    await new Promise((resolve) => setTimeout(resolve, 100));

    const previewPlaylist = await this.getControllerCacheAndParse(
      this.previewWindowData,
    );
    this.applyPreviewPlaylistToAudioMixer(previewPlaylist);

    // Trigger mode update (program already exists, so handler will skip creating it)
    const dateTimeUtcNow = moment().utc();
    const checkPeriodEndTime = dateTimeUtcNow
      .clone()
      .add(this._userService.getCurrentUser().liveBroadcastingCheckPeriodMs);
    await this._channelBroadcastModeChecker.triggerModeUpdate(
      dateTimeUtcNow,
      checkPeriodEndTime,
    );
    this._onSwitchItemBlockTypeChangedSubscription =
      this._broadcastsRepository.blockTypeChanged.subscribe((switcherItem) => {
        this.onSwitchItemBlockTypeChanged(switcherItem);
      });
    this._onHubReconnectedSubscription =
      this._socketService.onHubReconnected.subscribe(() => {
        this._resubscribeToStackAndChannelBroadcastUpdates();
      });
    // Subscribe again after mode update so reconnection and mode flow keep subscription in sync
    await this._resubscribeToStackAndChannelBroadcastUpdates();
    this.forceUIUpdate();
  }

  public onDestroy() {
    if (this._onBroadcastDeletedSubscription) {
      this._onBroadcastDeletedSubscription.unsubscribe();
      this._onBroadcastDeletedSubscription = null;
    }
    if (this._onHubReconnectedSubscription) {
      this._onHubReconnectedSubscription.unsubscribe();
      this._onHubReconnectedSubscription = null;
    }
    this._socketService.stopListeningForChannelBroadcastUpdates();
    this._socketService.stopListeningForLiveBroadcastStackAssetItemUpdates();
    if (this._intervalsPerChannelUid) {
      // tslint:disable-next-line:forin
      for (const channelUid in this._intervalsPerChannelUid) {
        clearInterval(this._intervalsPerChannelUid[channelUid]);
      }
      this._intervalsPerChannelUid = {};
    }
    if (this._channelBroadcastModeChangedSubscription) {
      this._channelBroadcastModeChangedSubscription.unsubscribe();
      this._channelBroadcastModeChangedSubscription = null;
    }
    // Clear all debounce timeouts
    if (this._channelBroadcastUpdatesDebounceTimeouts) {
      // tslint:disable-next-line:forin
      for (const channelUid in this._channelBroadcastUpdatesDebounceTimeouts) {
        clearTimeout(this._channelBroadcastUpdatesDebounceTimeouts[channelUid]);
      }
      this._channelBroadcastUpdatesDebounceTimeouts = {};
    }
    if (this._onSwitchItemBlockTypeChangedSubscription) {
      this._onSwitchItemBlockTypeChangedSubscription.unsubscribe();
      this._onSwitchItemBlockTypeChangedSubscription = null;
    }
    if (this._previewTimeoutId != null) {
      clearTimeout(this._previewTimeoutId);
      this._previewTimeoutId = null;
    }
    if (this._startLiveRefreshTimeoutId != null) {
      clearTimeout(this._startLiveRefreshTimeoutId);
      this._startLiveRefreshTimeoutId = null;
    }
    this._videoReconnectTimeoutIds.forEach((id) => clearTimeout(id));
    this._videoReconnectTimeoutIds = [];
    this.channelMediaListChanged.complete();
    this.channelsMap.clear();
    this.previewWindowData = null;
    this.programWindowData = null;
  }

  public deleteSwitcherClicked(
    event: MouseEvent,
    switcherToRemove: SwitcherViewModel,
  ) {
    event.preventDefault();
    event.stopPropagation();
    if (switcherToRemove.items.find((i) => i.isProgram || i.isPreview)) {
      this._ui.notify(this._ui.EVENTS.ShowAlert, {
        title: "Delete Switcher",
        message: `Can't delete the switcher since it contains preview/program asset!`,
      });

      return;
    }

    this._ui.notify(this._ui.EVENTS.ShowConfirm, {
      title: "Delete Switcher",
      message: `Are you sure that you want to delete ${switcherToRemove.name}?`,
      buttons: [
        {
          title: "Cancel",
          theme: "brigth",
          callback: () => {},
        },
        {
          title: "Remove",

          callback: async () => {
            if (this._duringDeleteSwitcher) {
              return;
            }
            try {
              this._duringDeleteSwitcher = true;
              const assetUidsToRemove = switcherToRemove.items.map(
                (i) => i.uid,
              );
              if (assetUidsToRemove && assetUidsToRemove.length > 0) {
                if (this.programWindowData) {
                  await this.executeControllerAction(
                    this.programWindowData,
                    ELiveBroadcastingAction.RemoveAssetItemsFromPrerollList,
                    assetUidsToRemove.join(","),
                  );
                }
                if (this.previewWindowData) {
                  await this.executeControllerAction(
                    this.previewWindowData,
                    ELiveBroadcastingAction.RemoveAssetItemsFromPrerollList,
                    assetUidsToRemove.join(","),
                  );
                }
              }

              const removingSelectedSwitcher =
                switcherToRemove === this.selectedSwitcher;
              this.switchers.splice(
                this.switchers.indexOf(switcherToRemove),
                1,
              );

              if (removingSelectedSwitcher) {
                this.toggleSwitch(this.switchersVMs[0]);
              }

              this.updateBroadcastPreset().then();
            } finally {
              this._duringDeleteSwitcher = false;
            }
          },
        },
      ],
    });
  }

  public toggleSwitch(switcher: SwitcherViewModel) {
    this.selectedSwitcher = switcher;
  }

  public async onAssetItemDelete(
    assetItem: SwitcherItemViewModel,
  ): Promise<void> {
    if (assetItem.isProgram || assetItem.isPreview) {
      this._ui.notify(this._ui.EVENTS.ShowAlert, {
        title: "Delete Asset",
        message: `Can't delete item from switcher, in use in preview/program!`,
      });
    } else {
      this._ui.notify(this._ui.EVENTS.ShowConfirm, {
        title: "Delete Asset",
        message: `Are you sure that you want to delete ${assetItem.assetName}?`,
        buttons: [
          {
            title: "Cancel",
            theme: "brigth",
            callback: () => {},
          },
          {
            title: "Remove",

            callback: async () => {
              if (this.programWindowData) {
                await this.executeControllerAction(
                  this.programWindowData,
                  ELiveBroadcastingAction.RemoveAssetItemsFromPrerollList,
                  assetItem.uid,
                );
              }
              if (this.previewWindowData) {
                await this.executeControllerAction(
                  this.previewWindowData,
                  ELiveBroadcastingAction.RemoveAssetItemsFromPrerollList,
                  assetItem.uid,
                );
              }
              await this.removeLiveFeedAudioChannelsFromBroadcastPreset(
                assetItem,
              );
              assetItem.toEmpty();
              this._broadcastsRepository.update(this).then();
              this._changeDetectorRef.markForCheck();
              this._changeDetectorRef.detectChanges();
            },
          },
        ],
      });
    }
  }
  async ManageAudioChannelOnChannel(
    channel: BroadcastPresetAudioChannel,
    muted: boolean,
    isProgram: boolean,
  ) {
    var window = isProgram ? this.programWindowData : this.previewWindowData;
    if (muted == false) {
      if (!isProgram && this.previewWindowData) {
        this.previewWindowData.isMuted = false;
      }
      await this._sendActionToLiveBroadcastingManager(
        window.channelUid,
        ELiveBroadcastingAction.AddLiveFeedAudioChannelToMediaList,
        channel.UniqueIdentifier,
        window.availabilityZoneIds,
      );
    } else {
      await this._sendActionToLiveBroadcastingManager(
        window.channelUid,
        ELiveBroadcastingAction.AddLiveFeedAudioChannelToPreroll,
        channel.UniqueIdentifier,
        window.availabilityZoneIds,
      );
    }
  }
  private getAudioChannelSourceUid(switcherItem: SwitcherItem): string {
    if (
      switcherItem.type === ESwitcherAssetType.Stack ||
      switcherItem.type === ESwitcherAssetType.Block
    ) {
      return switcherItem.uid;
    }
    return switcherItem.assetId.toString();
  }

  private isAudioChannelSourceStillInUse(
    switcherItem: SwitcherItem,
    sourceUid: string,
  ): boolean {
    return this.switchers.some((s) =>
      s.items.some((i) => {
        if (i.uid === switcherItem.uid || i.type === ESwitcherAssetType.Empty) {
          return false;
        }
        return this.getAudioChannelSourceUid(i) === sourceUid;
      }),
    );
  }

  public async removeLiveFeedAudioChannelsFromBroadcastPreset(
    assetItem: SwitcherItemViewModel,
  ): Promise<void> {
    var switcherItems: SwitcherItem[] = [assetItem];
    if (assetItem.type === ESwitcherAssetType.Layout) {
      switcherItems = await this.BreakdownLayoutSwitcherItem(assetItem);
    }
    for (const switcherItem of switcherItems) {
      const sourceUid = this.getAudioChannelSourceUid(switcherItem);
      if (this.isAudioChannelSourceStillInUse(switcherItem, sourceUid)) {
        continue;
      }
      this.audioChannels = this.audioChannels.filter(
        (ac) => ac.sourceUid !== sourceUid,
      );
    }
  }

  showScte35Tag(durationSeconds: number): void {
    this.clearScte35TagTimer();
    this.scte35TagActive = true;
    this.forceUIUpdate();
    this.scte35TagTimer = setTimeout(() => {
      this.hideScte35Tag();
      this.clearScte35TagTimer();
      this.forceUIUpdate();
    }, durationSeconds * 1000);
  }

  hideScte35Tag(): void {
    this.scte35TagActive = false;
    this.forceUIUpdate();
  }

  clearScte35TagTimer(): void {
    if (this.scte35TagTimer != null) {
      clearTimeout(this.scte35TagTimer);
      this.scte35TagTimer = null;
    }
  }

  async addScte35Marker(
    switcherItem: SwitcherItemViewModel,
    durationSeconds: number,
  ): Promise<void> {
    const uid = this._commonUtils.generateGuid();
    const tempItem = new SwitcherItem(
      uid,
      SWITCHER_ASSET_TYPE_SCTE35,
      switcherItem.index,
      "SCTE35",
      "",
      "",
      0,
      durationSeconds * 1000,
      false,
      false,
      false,
    );
    switcherItem.toEmpty();
    const tempVM = new SwitcherItemViewModel(
      tempItem,
      this._changeDetectorRef,
      this._ui,
      this._broadcastsRepository,
    );
    switcherItem.mergeWith(tempVM);
    switcherItem.uid = uid;
    await this.updateBroadcastPreset();
    switcherItem.isProccesing = true;
    if (this.previewWindowData) {
      await this.executeControllerAction(
        this.previewWindowData,
        ELiveBroadcastingAction.AddAssetItemsToPrerollList,
        switcherItem.uid,
      );
    }
    if (this.programWindowData) {
      await this.executeControllerAction(
        this.programWindowData,
        ELiveBroadcastingAction.AddAssetItemsToPrerollList,
        switcherItem.uid,
      );
    }
    switcherItem.isProccesing = false;
    this.forceUIUpdate();
  }

  async onProgramClicked(event: Event, switcherItem: SwitcherItemViewModel) {
    if (switcherItem.isProccesing) {
      return;
    }
    if (
      switcherItem.type == SWITCHER_ASSET_TYPE_SCTE35 &&
      this.programWindowData &&
      this._currentSelectedChannel &&
      this.programWindowData.channelUid !== this._currentSelectedChannel.uid
    ) {
      return;
    }
    const isGfxOrStack =
      switcherItem.type === ESwitcherAssetType.Gfx ||
      switcherItem.type === ESwitcherAssetType.Stack;
    // No-op for plain items already on program — must not take isProccesing (would lock the row).
    if (
      !isGfxOrStack &&
      switcherItem.isProgram &&
      switcherItem.type !== SWITCHER_ASSET_TYPE_SCTE35
    ) {
      return;
    }
    switcherItem.isProccesing = true;

    if (switcherItem.type === SWITCHER_ASSET_TYPE_SCTE35) {
      if (switcherItem.isProgram) {
        this.hideScte35Tag();
        this.clearScte35TagTimer();
        switcherItem.isProgram = false;
        await this.updateBroadcastPreset();
        switcherItem.isProccesing = false;
        this.forceUIUpdate();
        return;
      }
      this._turnOffProgramStateInAllItems();
      switcherItem.isProgram = true;
      const scteDurationSeconds = switcherItem.durationMs
        ? Math.max(0, Math.round(switcherItem.durationMs / 1000))
        : 0;
      this.showScte35Tag(scteDurationSeconds);
      await this.updateBroadcastPreset();
      switcherItem.isProccesing = false;
      this.channelMediaListChanged.next(true);
      this.forceUIUpdate();
      return;
    }

    const relevantItemUids = [];
    let payload = "";
    let postAction = false;
    let skipActionType = ELiveBroadcastingAction.SkipToItem;
    if (isGfxOrStack) {
      if (switcherItem.isProgram) {
        if (switcherItem.type === ESwitcherAssetType.Stack) {
          skipActionType = ELiveBroadcastingAction.SkipFromStack;
          payload = switcherItem.uid;
        } else {
          skipActionType = ELiveBroadcastingAction.RemoveItemFromPlaylist;
        }
      } else {
        if (switcherItem.type === ESwitcherAssetType.Stack) {
          skipActionType = ELiveBroadcastingAction.SkipToStack;
          payload = JSON.stringify({
            blockId: switcherItem.assetId,
            assetItemUid: switcherItem.uid,
          });
          this._advanceStackIndexIfCurrentItemNotVisible(switcherItem);
        } else {
          skipActionType = ELiveBroadcastingAction.SkipToSingleItem;
        }
      }

      switcherItem.isProgram = !switcherItem.isProgram;
    } else {
      var activeStack = this.switchers.flatMap((s) =>
        s.items.filter(
          (i) => i.isProgram && i.type == ESwitcherAssetType.Stack,
        ),
      );
      if (activeStack.length > 0) {
        skipActionType = ELiveBroadcastingAction.SkipToItemAndSkipFromStack;
        activeStack.map((s) => {
          relevantItemUids.push(`STACK_${s.uid}`);
        });
        postAction = true;
      }
      this._turnOffProgramStateInAllItems();
      switcherItem.isProgram = true;
    }

    relevantItemUids.push(switcherItem.uid);

    await this.updateBroadcastPreset();

    if (this.programWindowData) {
      if (!payload) {
        payload = [...new Set(relevantItemUids)].join(",");
      }
      await this.executeControllerAction(
        this.programWindowData,
        skipActionType,
        payload,
      );
    }

    switcherItem.isProccesing = false;
    this.channelMediaListChanged.next(true);
    this.forceUIUpdate();
    if (postAction) {
      relevantItemUids
        .filter((i) => (i as string).startsWith("STACK_"))
        .forEach((s: string) => {
          this._onLiveBroadcastStackAssetItemUpdatedEventHandler(
            this.programWindowData.channelUid,
            this.id,
            s.substring(6),
            true,
          );
        });
    }
  }

  async onPreviewClicked(event: Event, switcherItem: SwitcherItemViewModel) {
    if (switcherItem.isProccesing) {
      return;
    }
    const isGfxOrStackPreview =
      switcherItem.type === ESwitcherAssetType.Gfx ||
      switcherItem.type === ESwitcherAssetType.Stack;
    if (!isGfxOrStackPreview && switcherItem.isPreview) {
      return;
    }
    switcherItem.isProccesing = true;
    let payload = "";
    const relevantItemUids = [];
    let skipActionType = ELiveBroadcastingAction.SkipToItem;
    if (isGfxOrStackPreview) {
      if (switcherItem.isPreview) {
        if (switcherItem.type === ESwitcherAssetType.Stack) {
          skipActionType = ELiveBroadcastingAction.SkipFromStack;
          payload = switcherItem.uid;
        } else {
          skipActionType = ELiveBroadcastingAction.RemoveItemFromPlaylist;
        }
      } else {
        if (switcherItem.type === ESwitcherAssetType.Stack) {
          skipActionType = ELiveBroadcastingAction.SkipToStack;
          payload = JSON.stringify({
            blockId: switcherItem.assetId,
            assetItemUid: switcherItem.uid,
          });
          var previewTimeout = 200;
          var blockItemExtendedData =
            switcherItem.data as assetBlockItemExtendedData;
          if (blockItemExtendedData.assets) {
            var assetData = blockItemExtendedData.getBroadcastAssetItem(
              switcherItem.uid,
            );
            blockItemExtendedData.assets
              .slice(
                assetData.stackIndex,
                assetData.stackIndex + assetData.stackLength + 1,
              )
              .forEach((asset) => {
                previewTimeout += asset.duration;
              });
            if (this._previewTimeoutId != null) {
              clearTimeout(this._previewTimeoutId);
            }
            this._previewTimeoutId = setTimeout(() => {
              this._previewTimeoutId = null;
              var assetItem = this._getAssetItemByUid(switcherItem.uid);
              if (assetItem) {
                assetItem.isPreview = false;
                this.channelMediaListChanged.next(false);
                this.updateBroadcastPreset().then();
                this.forceUIUpdate();
              }
            }, previewTimeout);
          }
        } else {
          skipActionType = ELiveBroadcastingAction.SkipToSingleItem;
        }
      }

      switcherItem.isPreview = !switcherItem.isPreview;
    } else {
      this._turnOffPreviewStateInAllItems();
      switcherItem.isPreview = true;
    }

    relevantItemUids.push(switcherItem.uid);

    await this.updateBroadcastPreset();

    if (this.previewWindowData) {
      if (!payload) {
        payload = [...new Set(relevantItemUids)].join(",");
      }
      await this.executeControllerAction(
        this.previewWindowData,
        skipActionType,
        payload,
      );
    }

    switcherItem.isProccesing = false;
    this.channelMediaListChanged.next(false);
    this.forceUIUpdate();
  }
  public addNewSwitcher() {
    const switcherItems = [];
    for (let i = 0; i < 12; i++) {
      switcherItems.push(
        SwitcherItem.createEmptyItem(this._commonUtils.generateGuid(), i),
      );
    }
    this.switchers.push(
      new SwitcherViewModel(
        new Switcher(`Switcher ${this.switchers.length + 1}`, switcherItems),
        this._changeDetectorRef,
        this._ui,
        this._broadcastsRepository,
        this._blocksRepository,
      ),
    );
    this.updateBroadcastPreset().then();
  }

  public async getLivFeedAudioChannels(liveFeedUid: string) {
    const result = await this._sendActionToLiveBroadcastingManager(
      this.previewWindowData.channelUid,
      ELiveBroadcastingAction.GetLiveFeedAudioChannels,
      liveFeedUid,
      this.previewWindowData.availabilityZoneIds,
    );
    if (result) {
      return result.result;
    } else {
      return null;
    }
  }

  public async updateAudioChannelVolume(channel: BroadcastPresetAudioChannel) {
    this.audioChannels = this.audioChannels.map((c) =>
      c.sourceUid === channel.sourceUid && c.pid === channel.pid ? channel : c,
    );
    await this.updateBroadcastPreset();
    if (this.programWindowData) {
      await this._sendActionToLiveBroadcastingManager(
        this.programWindowData.channelUid,
        ELiveBroadcastingAction.UpdateAudioChannelVolume,
        channel.UniqueIdentifier,
        this.programWindowData.availabilityZoneIds,
      );
    }
    if (this.previewWindowData) {
      await this._sendActionToLiveBroadcastingManager(
        this.previewWindowData.channelUid,
        ELiveBroadcastingAction.UpdateAudioChannelVolume,
        channel.UniqueIdentifier,
        this.previewWindowData.availabilityZoneIds,
      );
    }
  }

  public async audioChannelUpdated(channel: BroadcastPresetAudioChannel) {
    this.audioChannels = this.audioChannels.map((c) =>
      c.sourceUid === channel.sourceUid && c.pid === channel.pid ? channel : c,
    );
    await this.updateBroadcastPreset();
  }

  private _sendActionsToLiveBroadcastingManager(
    requests: SendLiveBroadcastingActionRequest[],
  ): Promise<SendLiveBroadcastingActionResult[]> {
    return new Promise<SendLiveBroadcastingActionResult[]>(
      (resolve, reject) => {
        this._socketService.liveBroadcastingHubConnection
          .invoke("SendLiveBroadcastActionsAsync", requests)
          .then((result: SendLiveBroadcastingActionResult[]) => {
            result.forEach((r) => {
              try {
                var response = JSON.parse(r.result);
                var debugTag =
                  response.message && response.message.DebugTag
                    ? `[${response.message.DebugTag}]`
                    : "";
                console.log(
                  `BroadcastPresetViewModel->_sendActionsToLiveBroadcastingManager -> RESPONSE for ChannelUid: ${this.channelsMap[response.channelUid]} ${debugTag}\n${this._printResponseAsTextTable(response.message)}`,
                );
              } catch {}
            });
            requests.forEach((r) => {
              if (
                this.mode === EChannelBroadcastMode.PreLive &&
                this.allowLiveMode &&
                this.programWindowData != null &&
                this.programWindowData.channelUid === r.channelUid
              ) {
                // we need to update the operational channel cache
                this._sendActionToLiveBroadcastingManager(
                  this._currentSelectedChannel.uid,
                  ELiveBroadcastingAction.UpdateOperationalChannelCache,
                  r.channelUid,
                  r.availabilityZoneIds,
                );
                console.log(
                  `BroadcastPresetViewModel->_sendActionsToLiveBroadcastingManager -> sending UpdateOperationalChannelCache to operational channel cache`,
                );
              }
            });
            resolve(result);
          })
          .catch((err) => {
            console.error(err);
            reject(err);
          });
      },
    );
  }
  private _sendActionToLiveBroadcastingManager(
    channelUid: string,
    actionType: ELiveBroadcastingAction,
    payload: string,
    availabilityZoneIds: number[],
  ): Promise<SendLiveBroadcastingActionResult> {
    return new Promise<SendLiveBroadcastingActionResult>((resolve, reject) => {
      this._socketService.liveBroadcastingHubConnection
        .invoke("SendLiveBroadcastAction", {
          ChannelUid: channelUid,
          ActionType: actionType,
          Payload: payload,
          AvailabilityZoneIds: availabilityZoneIds,
        })
        .then((result: SendLiveBroadcastingActionResult) => {
          try {
            const response = JSON.parse(result.result);
            var debugTag =
              response.message && response.message.DebugTag
                ? `[${response.message.DebugTag}]`
                : "";
            console.log(
              `BroadcastPresetViewModel->_sendActionToLiveBroadcastingManager -> RESPONSE for ChannelUid: ${this.channelsMap[response.channelUid]} ${debugTag}\n${this._printResponseAsTextTable(response.message)}`,
            );
            if (
              this.mode === EChannelBroadcastMode.PreLive &&
              this.allowLiveMode &&
              this.programWindowData != null &&
              this.programWindowData.channelUid === channelUid
            ) {
              // we need to update the operational channel cache
              this._sendActionToLiveBroadcastingManager(
                this._currentSelectedChannel.uid,
                ELiveBroadcastingAction.UpdateOperationalChannelCache,
                channelUid,
                availabilityZoneIds,
              );
              console.log(
                `BroadcastPresetViewModel->_sendActionToLiveBroadcastingManager -> sending UpdateOperationalChannelCache to operational channel cache`,
              );
            }
          } catch {}
          resolve(result);
        })
        .catch((err) => {
          console.error(err);
          reject(err);
        });
    });
  }

  private _createWebRTCPreviewChannelForBroadcast(
    forProgramWindow: boolean,
    relatedRealChannelId: number,
  ): Promise<CreateBroadcastPreviewResult> {
    return new Promise<CreateBroadcastPreviewResult>((resolve, reject) => {
      this._socketService.liveBroadcastingHubConnection
        .invoke("CreateBroadcastPreview", {
          BroadcastPresetUid: this.id,
          isProgram: forProgramWindow,
          RelatedRealChannelId: relatedRealChannelId,
        })
        .then((result: CreateBroadcastPreviewResult) => {
          try {
            this.channelsMap[result.channelUid] = forProgramWindow
              ? "PROGRAM"
              : "PREVIEW";
          } catch {}

          resolve(result);
        })
        .catch((err) => {
          console.error(err);
          reject(err);
        });
    });
  }

  private _extendPreviewChannelExpirationDate(
    channelUid: string,
  ): Promise<ExtendPreviewChannelExpirationDateResult> {
    return new Promise<ExtendPreviewChannelExpirationDateResult>(
      (resolve, reject) => {
        this._socketService.liveBroadcastingHubConnection
          .invoke("ExtendPreviewChannelExpirationDate", {
            PreviewChannelUid: channelUid,
          })
          .then((result: ExtendPreviewChannelExpirationDateResult) => {
            resolve(result);
          })
          .catch((err) => {
            console.error(err);
            reject(err);
          });
      },
    );
  }

  private _getAllAssetItems(): SwitcherItem[] {
    return this.switchers.flatMap((i) => i.items);
  }
  private _getAssetItemByUid(uid: string): SwitcherItem | undefined {
    return this._getAllAssetItems().find((i) => i.uid === uid);
  }
  public getAllAssetItems(): SwitcherItem[] {
    return this._getAllAssetItems();
  }
  public getAssetsInPreview(): SwitcherItem[] {
    return this._getAllAssetItems().filter((i) => i.isPreview);
  }
  public getAssetsInProgram(): SwitcherItem[] {
    return this._getAllAssetItems().filter((i) => i.isProgram);
  }

  private _turnOffProgramStateInAllItems() {
    this.switchers.forEach((switcher) => {
      switcher.items.forEach(
        (switcherItem) => (switcherItem.isProgram = false),
      );
    });
  }
  private _turnOffPreviewStateInAllItems() {
    this.switchers.forEach((switcher) => {
      switcher.items.forEach(
        (switcherItem) => (switcherItem.isPreview = false),
      );
    });
  }

  onBroadcastPresetNameEdited() {
    this.updateBroadcastPreset().then();
  }

  onDuplicateClicked() {
    const clonedBroadcastPreset = this.clone(this._commonUtils.generateGuid());
    this._broadcastsRepository.create(clonedBroadcastPreset).then(() => {
      return this._router.navigate([
        "/broadcasts/edit",
        clonedBroadcastPreset.id,
      ]);
    });
  }

  onSwitcherNameEdited(event: FocusEvent, switcher: SwitcherViewModel) {
    this.updateBroadcastPreset().then(() => {
      switcher.nameCanBeEdited = false;
      const element = event.target as HTMLElement;
      element.textContent =
        switcher === this.selectedSwitcher ? switcher.name : switcher.nameVM;
      // Place cursor before first child element (if any)
      setTimeout(() => {
        const selection = window.getSelection();
        const range = document.createRange();

        const firstChild = element.firstChild;

        if (firstChild) {
          range.setStartBefore(firstChild); // Before the first node
        } else {
          range.setStart(element, 0); // If empty, set start of container
        }

        range.collapse(true); // Collapse to insertion point
        selection?.removeAllRanges();
        selection?.addRange(range);
      }, 50);
    });
  }

  async onDrop(event: DragEvent, switcherItem: SwitcherItemViewModel) {
    console.log("SwitcherViewModel->onDrop", event, switcherItem);

    console.log(
      "SwitcherViewModel->onDroptypes:",
      event.dataTransfer.types.join(","),
    );
    let draggedLayout: Layout;
    if (
      event.dataTransfer &&
      event.dataTransfer.types &&
      event.dataTransfer.types.length > 0
    ) {
      const tempItem = new SwitcherItemViewModel(
        SwitcherItem.createEmptyItem(switcherItem.uid, switcherItem.index),
        this._changeDetectorRef,
        this._ui,
        this._broadcastsRepository,
      );

      for (let i = 0; i < event.dataTransfer.types.length; i++) {
        const mediaType = event.dataTransfer.types[i];
        switch (mediaType) {
          case "text/media":
            const draggedMedia: MediaItem = JSON.parse(
              event.dataTransfer.getData("text/media"),
            );
            /*const mediaInfo = await this._mediaRepository.getMediaInfo(draggedMedia.id);
              if (mediaInfo && mediaInfo.segments.length > 0) {
                await this._mediaItemWithSegmentsDraggedEventHandler(mediaInfo, zone);
                break;
              }*/
            tempItem.assetName = draggedMedia.name;
            tempItem.assetId = draggedMedia.id;
            tempItem.previewUrl = draggedMedia.contentSnapshotUrl;
            tempItem.durationMs = draggedMedia.contentMediaDurationMs;
            tempItem.type = ESwitcherAssetType.Media;
            break;
          case "text/feed":
            const draggedLiveFeed: LiveFeedInfo = JSON.parse(
              event.dataTransfer.getData("text/feed"),
            );
            tempItem.assetName = draggedLiveFeed.name;
            tempItem.assetId = draggedLiveFeed.id;
            tempItem.previewUrl = draggedLiveFeed.thumbnail;
            tempItem.durationMs = 24 * 60 * 60 * 60 * 1000;
            tempItem.type = ESwitcherAssetType.LiveFeed;
            break;

          case "text/graphics":
            const draggedGraphicsInfo: GraphicsInfo = JSON.parse(
              event.dataTransfer.getData("text/graphics"),
            );
            if (draggedGraphicsInfo.isTemplate === false) {
              tempItem.assetName = draggedGraphicsInfo.name;
              tempItem.assetId = draggedGraphicsInfo.id;
              tempItem.previewUrl = draggedGraphicsInfo.thumbnail;
              tempItem.durationMs = 24 * 60 * 60 * 60 * 1000;
              tempItem.type = ESwitcherAssetType.Gfx;
            }

            break;

          case "text/layout":
            draggedLayout = JSON.parse(
              event.dataTransfer.getData("text/layout"),
            );
            tempItem.assetName = draggedLayout.name;
            tempItem.assetId = draggedLayout.id;
            tempItem.previewUrl = draggedLayout.zones[0].mediaThumbnailUrl;
            tempItem.durationMs =
              draggedLayout.zones[0].durationMs ?? 24 * 60 * 60 * 60 * 1000;
            tempItem.somMs = draggedLayout.zones[0].somMs;
            tempItem.type = ESwitcherAssetType.Layout;
            break;

          case "text/block":
            const draggedBlock: Block = JSON.parse(
              event.dataTransfer.getData("text/block"),
            );
            tempItem.assetName = draggedBlock.name;
            tempItem.assetId = draggedBlock.id;
            tempItem.previewUrl = draggedBlock.packshotUrl;
            if (draggedBlock.duration) {
              const d = moment.duration(draggedBlock.duration);
              tempItem.durationMs = d.asMilliseconds();
            }
            tempItem.assetsCount = draggedBlock.showsCount;
            tempItem.somMs = 0;
            tempItem.type = draggedBlock.isBlockStack
              ? ESwitcherAssetType.Stack
              : ESwitcherAssetType.Block;
            break;
        }
        if ((await this._validateItemOnDrop(tempItem)) === true) {
          switcherItem.toEmpty();
          switcherItem.mergeWith(tempItem);
          switcherItem.resetState();

          // should be the new asset item
          switcherItem.uid = this._commonUtils.generateGuid();
          await this.updateBroadcastPreset();

          switcherItem.isProccesing = true;

          if (this.previewWindowData) {
            await this.executeControllerAction(
              this.previewWindowData,
              ELiveBroadcastingAction.AddAssetItemsToPrerollList,
              switcherItem.uid,
            );
          }

          if (this.programWindowData) {
            await this.executeControllerAction(
              this.programWindowData,
              ELiveBroadcastingAction.AddAssetItemsToPrerollList,
              switcherItem.uid,
            );
          }

          switcherItem.isProccesing = false;
          const audioChannelForNewSwitcherItem = async (
            switcherItem: SwitcherItemViewModel,
          ) => {
            var switcherItems: Array<SwitcherItem> = [];
            if (switcherItem.type === ESwitcherAssetType.Layout) {
              var brokenDownSwitcherItems =
                await this.BreakdownLayoutSwitcherItem(switcherItem);
              switcherItems.push(...brokenDownSwitcherItems);
            } else {
              switcherItems.push(switcherItem);
            }
            for (let i = 0; i < switcherItems.length; i++) {
              var si = switcherItems[i];
              const id = this.getAudioChannelSourceUid(si);
              if (
                this.audioChannels.some((ac) => ac.sourceUid === id) === false
              ) {
                if (switcherItems[i].type === ESwitcherAssetType.LiveFeed) {
                  await this.populateLiveFeedAudioChannels(si);
                } else {
                  var audioChannel = BroadcastPresetAudioChannel.createNew(
                    si.type,
                    si.assetName,
                    id,
                    this._currentSelectedChannelInfo.mainLanguageCode,
                  );
                  audioChannel.isVisible = false;
                  audioChannel.isMuted = false;
                  this.audioChannels.push(audioChannel);
                }
              }
            }
            await this.updateBroadcastPreset();
          };
          if (
            switcherItem.type === ESwitcherAssetType.Block ||
            switcherItem.type === ESwitcherAssetType.Stack
          ) {
            switcherItem.data =
              await this._broadcastsRepository.getBlockExtendedData(
                this.id,
                switcherItem,
              );
            var switcher = this.switchers.find((switcher) =>
              switcher.items.some((item) => item.uid === switcherItem.uid),
            );
            if (switcher) {
              (switcher as SwitcherViewModel).updateItems([switcherItem]);
            }
            await this._broadcastsRepository.refreshBroadcastPreset(this.id);
          }
          if (
            switcherItem.type !== ESwitcherAssetType.LiveFeed &&
            switcherItem.type !== ESwitcherAssetType.Gfx
          ) {
            await audioChannelForNewSwitcherItem(switcherItem);
          } else if (
            switcherItem.type === ESwitcherAssetType.LiveFeed &&
            this.audioChannels.some(
              (ac) => ac.sourceUid === switcherItem.assetId.toString(),
            ) === false
          ) {
            await this.populateLiveFeedAudioChannels(switcherItem);
          }
          this.forceUIUpdate();
        } else {
          switcherItem.resetState();
        }
      }
    }
    this.forceUIUpdate();
  }

  public async BreakdownLayoutSwitcherItem(
    layoutSwitcherItem: SwitcherItem,
  ): Promise<SwitcherItem[]> {
    var reusltSwitcherItems: SwitcherItem[] = [];
    var layoutsResult = await this._layoutRepository.getLayouts(
      null,
      layoutSwitcherItem.assetId.toString(),
    );
    if (layoutsResult.length > 0) {
      var layout = layoutsResult[0];
      layout.zones.forEach((zone) => {
        var assetType = ESwitcherAssetType.Empty;
        if (zone.zoneType === ELayoutZoneType.media) {
          assetType = ESwitcherAssetType.Media;
        } else if (zone.zoneType === ELayoutZoneType.liveFeed) {
          assetType = ESwitcherAssetType.LiveFeed;
        } else if (zone.zoneType === ELayoutZoneType.gfx) {
          assetType = ESwitcherAssetType.Gfx;
        }
        var newSwitcherItem = new SwitcherItem(
          this._commonUtils.generateGuid(),
          assetType,
          zone.index,
          zone.mediaName,
          zone.mediaThumbnailUrl,
          zone.mediaId,
          zone.somMs,
          zone.durationMs ?? 24 * 60 * 60 * 60 * 1000,
          false,
          false,
          false,
        );
        reusltSwitcherItems.push(newSwitcherItem);
      });
    }
    return reusltSwitcherItems;
  }

  private async populateLiveFeedAudioChannels(
    switcherItem: SwitcherItem,
  ): Promise<void> {
    const response = await this.getLivFeedAudioChannels(
      switcherItem.assetId.toString(),
    );
    if (response && typeof response === "string") {
      var audioChannelsUniqueIdentifiers = new Set<string>();
      try {
        const liveFeedAudioChannels = JSON.parse(response);
        var audioChannelsCount = this.audioChannels.length;
        for (let i = 0; i < liveFeedAudioChannels.length; i++) {
          const liveFeedAudioChannel = liveFeedAudioChannels[i];
          if (liveFeedAudioChannel.FrameRate) {
            continue;
            // this is a video
          }
          const newAudioChannel = BroadcastPresetAudioChannel.createNew(
            ESwitcherAssetType.LiveFeed,
            `${switcherItem.assetName}_PID-${liveFeedAudioChannel.PID}`,
            switcherItem.assetId.toString(),
            liveFeedAudioChannel.LanguageCode,
            liveFeedAudioChannel.PID,
            1.0,
          );
          this.audioChannels.push(newAudioChannel);
          audioChannelsUniqueIdentifiers.add(newAudioChannel.UniqueIdentifier);
        }
        if (audioChannelsCount < this.audioChannels.length) {
          await this.updateBroadcastPreset();
          await this.executeControllerAction(
            this.previewWindowData,
            ELiveBroadcastingAction.AddLiveFeedAudioChannelToPreroll,
            Array.from(audioChannelsUniqueIdentifiers).join(","),
          );
          await this.executeControllerAction(
            this.programWindowData,
            ELiveBroadcastingAction.AddLiveFeedAudioChannelToPreroll,
            Array.from(audioChannelsUniqueIdentifiers).join(","),
          );
        }
      } catch (error) {
        console.error("Error parsing live feed audio channels:", error);
      }
    }
  }
  async updateBroadcastPreset(): Promise<void> {
    await this._broadcastsRepository.update(this._asBaseClass());
    this.forceUIUpdate();
  }

  async onVideoStartedToPlay(
    event: Event,
    previewModel: WebRTCPreviewViewModel,
  ) {
    console.log(
      `BroadcastPresetViewModel->onVideoStartedToPlay:`,
      event,
      previewModel,
    );
    previewModel.previewVideoHasStartedPlaying = true;
    this.startPollingPreviewChannelExpirationExtended(previewModel);
    // const response = await this._sendActionToLiveBroadcastingManager(previewModel.channelUid, ELiveBroadcastingAction.UpdateEngineChannelPlaylist, '', previewModel.availabilityZoneId);
    // console.log(`BroadcastPresetViewModel->onVideoStartedToPlay->_sendActionToLiveBroadcastingManager->RESPONSE`, response);
  }
  private startPollingPreviewChannelExpirationExtended(
    previewModel: WebRTCPreviewViewModel,
  ) {
    const existingInterval =
      this._intervalsPerChannelUid[previewModel.channelUid];
    if (existingInterval) {
      clearInterval(existingInterval);
    }
    this._intervalsPerChannelUid[previewModel.channelUid] = setInterval(
      async () => {
        const extendResponse = await this._extendPreviewChannelExpirationDate(
          previewModel.channelUid,
        );
        if (extendResponse.result !== "OK") {
          clearInterval(this._intervalsPerChannelUid[previewModel.channelUid]);
        }
        console.log(
          `BroadcastPresetViewModel->startPollingPreviewChannelExpirationExtended-> Extending expiration date of the ${previewModel.isProgram ? "PROGRAM" : "PREVIEW"} channel ${previewModel.channelUid}. Result: ${extendResponse.result}`,
        );
      },
      10 * 1000,
    );
  }

  public updateSwitchers(switchers: Switcher[]) {
    var blockItems = switchers.flatMap((switcher) =>
      switcher.items.filter(
        (item) =>
          item.type === ESwitcherAssetType.Block ||
          item.type === ESwitcherAssetType.Stack,
      ),
    );
    if (blockItems && blockItems.length > 0) {
      blockItems.forEach((blockItem) => {
        var switcher = this.switchers.find((switcher) =>
          switcher.items.some((item) => item.uid === blockItem.uid),
        );
        if (switcher) {
          (switcher as SwitcherViewModel).updateItems([blockItem]);
        }
      });
    }
    this.forceUIUpdate();
  }

  public forceUIUpdate() {
    this._changeDetectorRef.markForCheck();
    this._changeDetectorRef.detectChanges();
  }

  turnOnAudio(webRtcWindowData: WebRTCPreviewViewModel) {
    webRtcWindowData.isMuted = !webRtcWindowData.isMuted;
    // if (webRtcWindowData.isProgram) {
    //   this.programWindowData.isMuted = !this.programWindowData.isMuted;
    // } else {
    //   this.previewWindowData.isMuted = !this.previewWindowData.isMuted;
    // }
  }

  async sendEmptyPlaylist(previewModel: WebRTCPreviewViewModel) {
    await this.executeControllerAction(
      previewModel,
      ELiveBroadcastingAction.SendEmptyList,
      "",
    );
  }

  async sendFullPlaylist(previewModel: WebRTCPreviewViewModel) {
    await this.executeControllerAction(
      previewModel,
      ELiveBroadcastingAction.UpdateEngineChannelFullPlaylist,
      "",
    );
  }

  async createWebrtcChannel(forProgramWindow: boolean) {
    const startTime = new Date();

    this.lastCommandProcessingTimeMs = 0;
    this.lastCommandName = "Create WebRTC Channel";
    this.lastCommandResponse = "Processing...";
    const createWebrtcForPreviewResult =
      await this._createWebRTCPreviewChannelForBroadcast(
        forProgramWindow,
        this._currentSelectedChannel.id,
      );
    if (forProgramWindow) {
      // tslint:disable-next-line:max-line-length
      this.programWindowData = this.createWebRTCPreviewViewModel(
        true,
        createWebrtcForPreviewResult.clientId,
        createWebrtcForPreviewResult.edgeBrodcasterId,
        [createWebrtcForPreviewResult.url],
        true,
        this.debugMode,
        createWebrtcForPreviewResult.channelUid,
        [createWebrtcForPreviewResult.availabilityZoneId],
        "",
      );
    } else {
      // tslint:disable-next-line:max-line-length
      this.previewWindowData = this.createWebRTCPreviewViewModel(
        false,
        createWebrtcForPreviewResult.clientId,
        createWebrtcForPreviewResult.edgeBrodcasterId,
        [createWebrtcForPreviewResult.url],
        true,
        this.debugMode,
        createWebrtcForPreviewResult.channelUid,
        [createWebrtcForPreviewResult.availabilityZoneId],
        "",
      );
    }
    this.lastCommandResponse = `[${new Date().toTimeString()}]Channel ${createWebrtcForPreviewResult.channelUid} has been activated!`;
    console.log(`BroadcastPresetViewModel->createWebrtcChannel->Created`);
    this.forceUIUpdate();

    if (!forProgramWindow && this.previewWindowData) {
      const previewPlaylist = await this.getControllerCacheAndParse(
        this.previewWindowData,
      );
      this.applyPreviewPlaylistToAudioMixer(previewPlaylist);
    }

    const endTime = new Date();
    this.lastCommandProcessingTimeMs = endTime.getTime() - startTime.getTime();
  }

  async deactivateWebRTCChannel(previewWindowData: WebRTCPreviewViewModel) {
    const startTime = new Date();

    this.lastCommandProcessingTimeMs = 0;
    this.lastCommandName = "Deactivate WebRTC Preview Channel";
    this.lastCommandResponse = "Processing...";
    await this._socketService.deletePreviewChannel(
      previewWindowData.channelUid,
    );
    this.lastCommandResponse = `[${new Date().toTimeString()}]Channel ${previewWindowData.channelUid} has been deactivated`;
    this.forceUIUpdate();

    if (previewWindowData === this.previewWindowData) {
      this.previewWindowData = null;
    } else {
      this.programWindowData = null;
    }

    const endTime = new Date();
    this.lastCommandProcessingTimeMs = endTime.getTime() - startTime.getTime();
  }

  async sendPlaylistWithoutPrerolls(previewModel: WebRTCPreviewViewModel) {
    await this.executeControllerAction(
      previewModel,
      ELiveBroadcastingAction.UpdateEngineChannelPlaylistWithoutPreroll,
      "",
    );
  }

  async invalidateCache(previewWindowData: WebRTCPreviewViewModel) {
    await this.executeControllerAction(
      previewWindowData,
      ELiveBroadcastingAction.InvalidateControllerCache,
      "",
    );
  }

  async getControllerCache(previewWindowData: WebRTCPreviewViewModel) {
    await this.executeControllerAction(
      previewWindowData,
      ELiveBroadcastingAction.GetControllerCache,
      "",
    );
  }

  async getControllerCacheAndParse(
    previewWindowData: WebRTCPreviewViewModel,
  ): Promise<PlaylistDto | null> {
    try {
      const result = await this._sendActionToLiveBroadcastingManager(
        previewWindowData.channelUid,
        ELiveBroadcastingAction.GetControllerCache,
        "",
        previewWindowData.availabilityZoneIds,
      );
      const response = JSON.parse(result.result);
      const message = response.message;
      if (typeof message !== "string") {
        return message && typeof message === "object" ? message : null;
      }
      const playlist = JSON.parse(message) as PlaylistDto;
      if (
        !playlist ||
        typeof playlist !== "object" ||
        (!playlist.MediaList && !playlist.PrerolledMedia)
      ) {
        return null;
      }
      return playlist;
    } catch {
      return null;
    }
  }

  applyPreviewPlaylistToAudioMixer(playlist: PlaylistDto | null): void {
    if (!playlist?.MediaList || !this.previewWindowData) {
      return;
    }
    const mediaListValues = Object.values(playlist.MediaList);
    const nowUtcMs = Date.now();
    let anyPreviewAudioPlaying = false;
    for (const channel of this.audioChannels) {
      if (!channel.isLiveFeed) {
        continue;
      }
      const suffix = `_audio_stream_PID_${channel.pid}`;
      const stillPlayingItem = mediaListValues.find((item) => {
        const mediaType =
          typeof item.MediaType === "string" ? item.MediaType : item.MediaType;
        if (
          mediaType !== STREAM_IN_AUDIO_MEDIA_TYPE &&
          mediaType !== "StreamInAudio"
        ) {
          return false;
        }
        if (
          !item.ItemUID ||
          !item.ItemUID.includes(channel.sourceUid) ||
          !item.ItemUID.includes(suffix)
        ) {
          return false;
        }
        const startTime = item.StartTime;
        const durationMs = item.DurationMS;
        if (
          startTime == null ||
          durationMs == null ||
          typeof durationMs !== "number"
        ) {
          return false;
        }
        const startMs =
          typeof startTime === "string"
            ? new Date(startTime).getTime()
            : Number(startTime);
        if (isNaN(startMs)) {
          return false;
        }
        const itemEndMs = startMs + durationMs;
        return nowUtcMs < itemEndMs;
      });
      channel.previewListenMuted = stillPlayingItem == null;
      if (stillPlayingItem != null) {
        anyPreviewAudioPlaying = true;
        channel.isVisible = true;
      }
    }
    // Only unmute after the video has started playing (avoids NotAllowedError: play() failed because the user didn't interact with the document first).
    if (
      anyPreviewAudioPlaying &&
      this.previewWindowData &&
      this.previewWindowData.previewVideoHasStartedPlaying
    ) {
      this.previewWindowData.isMuted = false;
    }
    this.channelMediaListChanged.next(false);
    this.forceUIUpdate();
  }

  private async executeControllerAction(
    previewWindowData: WebRTCPreviewViewModel,
    actionType: ELiveBroadcastingAction,
    payload: string,
  ) {
    const startTime = new Date();
    this.lastCommandProcessingTimeMs = 0;
    this.lastCommandName = ELiveBroadcastingAction[actionType].toString();

    this.lastCommandResponse = "Processing...";
    previewWindowData.setStatusText(actionType);
    this.forceUIUpdate();
    const response = await this._sendActionToLiveBroadcastingManager(
      previewWindowData.channelUid,
      actionType,
      payload,
      previewWindowData.availabilityZoneIds,
    );
    previewWindowData.clearStatus();
    this.forceUIUpdate();

    this.lastCommandResponse = response.result;

    const endTime = new Date();

    this.lastCommandProcessingTimeMs = endTime.getTime() - startTime.getTime();
  }
  private _formatAudioLanguages(mediaItem: any): string {
    if (!mediaItem) {
      return "";
    }

    const languageVolumeKeyframes = mediaItem["LanguageVolumeKeyframes"];
    const languageVolumes = mediaItem["LanguageVolumes"];

    // Check if LanguageVolumeKeyframes exists and is not empty
    if (
      languageVolumeKeyframes &&
      typeof languageVolumeKeyframes === "object" &&
      Object.keys(languageVolumeKeyframes).length > 0
    ) {
      // Process LanguageVolumeKeyframes: object of objects (dictionary)
      const result: string[] = [];
      for (const languageName in languageVolumeKeyframes) {
        const keyframes = languageVolumeKeyframes[languageName];
        if (keyframes && typeof keyframes === "object") {
          // Get all numeric keys and find the biggest one (last keyframe)
          const numericKeys = Object.keys(keyframes)
            .map((k) => Number(k))
            .filter((k) => !isNaN(k))
            .sort((a, b) => a - b);

          if (numericKeys.length > 0) {
            const lastKeyframe = numericKeys[numericKeys.length - 1];
            const lastValue = keyframes[lastKeyframe.toString()];
            const formattedValue = parseFloat(lastValue).toFixed(2);
            result.push(`${languageName}:${formattedValue}`);
          }
        }
      }
      return result.join(", ");
    } else if (
      languageVolumes &&
      typeof languageVolumes === "object" &&
      Object.keys(languageVolumes).length > 0
    ) {
      // Process LanguageVolumes: simple key-value object
      const result: string[] = [];
      for (const languageName in languageVolumes) {
        const value = languageVolumes[languageName];
        const formattedValue = parseFloat(value).toFixed(2);
        result.push(`${languageName}:${formattedValue}`);
      }
      return result.join(", ");
    }

    return "";
  }
  private _printResponseAsTextTable(response: any): string {
    if (typeof response === "string") {
      return response;
    }
    const resultTableObj = [];

    const rowTemplate = {
      Property: "",
      ItemUID: "",
      ItemName: "",
      StartTime: "",
      DurationMS: "",
      EndTime: "",
      AudioLanguages: "",
    };

    const playlistStartTimeRow = { ...rowTemplate };
    playlistStartTimeRow.Property = "Playlist";
    playlistStartTimeRow.StartTime = moment
      .utc(response.StartTime)
      .format("YYYY-MM-DD HH:mm:ss.SSS");
    playlistStartTimeRow.EndTime = moment
      .utc(response.EndTime)
      .format("YYYY-MM-DD HH:mm:ss.SSS");
    resultTableObj.push(playlistStartTimeRow);

    const mediaListHeader = { ...rowTemplate };
    mediaListHeader.Property = "MediaList";
    resultTableObj.push(mediaListHeader);

    const mediaListRows = [];
    for (const itemUid in response["MediaList"]) {
      const tempRow = { ...rowTemplate };
      const mediaItem = response["MediaList"][itemUid];

      tempRow.ItemUID = itemUid;
      tempRow.ItemName = mediaItem["ItemName"];
      tempRow.StartTime = moment
        .utc(mediaItem["StartTime"])
        .format("YYYY-MM-DD HH:mm:ss.SSS");
      tempRow.DurationMS = mediaItem["DurationMS"];
      tempRow.EndTime = moment
        .utc(tempRow.StartTime)
        .add(tempRow.DurationMS)
        .format("YYYY-MM-DD HH:mm:ss.SSS");
      tempRow.AudioLanguages = this._formatAudioLanguages(mediaItem);

      mediaListRows.push(tempRow);
    }
    mediaListRows.sort((a, b) => a.StartTime.localeCompare(b.StartTime));
    mediaListRows.forEach((i) => resultTableObj.push(i));

    const prerolledMediaHeader = { ...rowTemplate };
    prerolledMediaHeader.Property = "PrerolledMedia";
    resultTableObj.push(prerolledMediaHeader);

    const prerollListRows = [];
    for (const itemUid in response["PrerolledMedia"]) {
      const tempRow = { ...rowTemplate };
      const mediaItem = response["PrerolledMedia"][itemUid];

      tempRow.ItemUID = itemUid;
      tempRow.ItemName = mediaItem["ItemName"];
      tempRow.StartTime = moment
        .utc(mediaItem["StartTime"])
        .format("YYYY-MM-DD HH:mm:ss.SSS");
      tempRow.DurationMS = mediaItem["DurationMS"];
      tempRow.EndTime = moment
        .utc(tempRow.StartTime)
        .add(tempRow.DurationMS)
        .format("YYYY-MM-DD HH:mm:ss.SSS");
      tempRow.AudioLanguages = this._formatAudioLanguages(mediaItem);

      prerollListRows.push(tempRow);
    }
    prerollListRows.sort((a, b) => a.StartTime.localeCompare(b.StartTime));
    prerollListRows.forEach((i) => resultTableObj.push(i));

    return this._formatTable(resultTableObj);
  }
  private _formatTable(objects: Record<string, any>[]): string {
    if (objects.length === 0) {
      return "No data to display.";
    }

    // Get all unique keys from the array of objects
    const keys = Array.from(
      new Set(objects.flatMap((obj) => Object.keys(obj))),
    );

    // Determine column widths
    const columnWidths = keys.map((key) =>
      Math.max(
        key.length,
        ...objects.map((obj) => String(obj[key] ?? "").length),
      ),
    );

    // Helper function to create a separator line
    const separator = `+${columnWidths.map((w) => "-".repeat(w + 2)).join("+")}+`;

    // Format the header row
    const header = `| ${keys.map((key, i) => key.padEnd(columnWidths[i])).join(" | ")} |`;

    // Format each row of values
    const rows = objects.map(
      (obj) =>
        `| ${keys.map((key, i) => String(obj[key] ?? "").padEnd(columnWidths[i])).join(" | ")} |`,
    );

    // Combine everything into a table string
    return [separator, header, separator, ...rows, separator].join("\n");
  }
  private async _channelLiveBroadcastingModeChanged(
    modeUpdated: BroadcastModeUpdate,
  ): Promise<void> {
    // While live, the program window must point at the operational channel.
    // If it doesn't (e.g. allowLiveMode was still false the first time we
    // handled a Live update), don't let the same-mode guard below short-circuit
    // us - otherwise the window stays on the preset's program channel forever.
    const programWindowIsStale =
      this.mode === EChannelBroadcastMode.Live &&
      this.allowLiveMode &&
      this.programWindowData != null &&
      this._currentSelectedChannel != null &&
      this.programWindowData.channelUid !== this._currentSelectedChannel.uid;
    if (
      !modeUpdated.fromUserStartLive &&
      this.programWindowData != null &&
      this.previewWindowData != null &&
      ((this.mode === modeUpdated.mode && !programWindowIsStale) ||
        this.duringModeUpdate)
    ) {
      return;
    }
    try {
      this.duringModeUpdate = true;
      const preMode = this.mode;

      if (modeUpdated.fromUserStartLive) {
        this.mode = modeUpdated.mode;
      } else if (this._currentSelectedChannel) {
        // Always derive mode from the current channel's authoritative state,
        // regardless of which channel triggered the update. This prevents
        // a Stop Live on channel A from resetting the VM of channel B.
        const channelState =
          this._channelBroadcastModeChecker.getStateForChannel(
            this._currentSelectedChannel.id,
          );
        if (channelState) {
          // check if the channel is live and the broadcast preset playing in the channel is not the same as this one
          if (
            (channelState.liveBroadcastPresetShowUid ||
              channelState.nextLiveBroadcastPresetShow) &&
            channelState.mode !== EChannelBroadcastMode.Off
          ) {
            this.mode = channelState.mode;
            let show = null;
            if (channelState.mode === EChannelBroadcastMode.Live) {
              //this.allowLiveMode = true;
              show = this._calendarService.getShowFromCache(
                channelState.liveBroadcastPresetShowUid,
              );
              if (!show) {
                const now = moment().utc();
                const shows = await this._calendarService.getByDates(
                  this._currentSelectedChannel.id,
                  now,
                  now.clone().add(1, "hour"),
                );
                show = shows.find(
                  (s) => s.id === channelState.liveBroadcastPresetShowUid,
                );
              }
              if (show) {
                await this._channelRepository.updateLiveBroadcastShowUid(
                  this._currentSelectedChannel.id,
                  show.id,
                );
              }
            } else if (channelState.mode === EChannelBroadcastMode.PreLive) {
              show = channelState.nextLiveBroadcastPresetShow;
            }
            if (
              show instanceof BroadcastPresetShow &&
              show.broadcastPresetUid !== this.id
            ) {
              this.mode = EChannelBroadcastMode.Off;
              this.editMode = true;
            } else if (
              show instanceof BroadcastPresetShow &&
              show.broadcastPresetUid === this.id
            ) {
              this.editMode = false;
            }
          } else {
            // Current channel has no active broadcast.
            // Only apply Off-mode transition if the update is actually for
            // the current channel or is a global reset (all channels off).
            // Ignore events that belong to a different channel.
            if (
              modeUpdated.channelId === this._currentSelectedChannel.id ||
              modeUpdated.channelId === null
            ) {
              this.mode = channelState.mode; // Off
              this.editMode = true;
            }
          }
        }
      }
      if (
        (preMode === EChannelBroadcastMode.PreLive ||
          preMode === EChannelBroadcastMode.Live) &&
        this.mode === EChannelBroadcastMode.Off
      ) {
        //the channel went from live to offline mode, so we have to refresh the program window
        this.programWindowData = null;
        this.editMode = true;
        this.allowLiveMode = false;
        this.clearExistingProgramWindowInterval();
      }
      this._socketService.stopListeningForChannelBroadcastUpdates();
      console.log(
        `broadcast-preset.vm->_channelLiveBroadcastingModeChanged->this.mode=`,
        EChannelBroadcastMode[this.mode],
      );
      if (this.mode === EChannelBroadcastMode.Live && this.allowLiveMode) {
        const createRealChannelPreviewResponse =
          await this._socketService.createChannelWebRTCPreview(
            this._currentSelectedChannel,
          );
        console.log(
          `broadcast-preset.vm->_channelLiveBroadcastingModeChanged->createRealChannelPreviewResponse `,
          createRealChannelPreviewResponse,
          this.programWindowData,
          this._currentSelectedChannel.uid,
          !this.programWindowData ||
            this.programWindowData.channelUid !==
              this._currentSelectedChannel.uid,
        );
        if (
          !(
            this.programWindowData &&
            this.programWindowData.channelUid ===
              this._currentSelectedChannel.uid
          )
        ) {
          console.log(
            `broadcast-preset.vm->_channelLiveBroadcastingModeChanged-> mode is LIVE. Creating a new program window`,
          );
          this.clearExistingProgramWindowInterval();
          // Kill the broadcast preset's program channel before switching to the operational channel.
          if (
            this.programWindowData &&
            this.programWindowData.channelUid !==
              this._currentSelectedChannel.uid
          ) {
            await this.deactivateWebRTCChannel(this.programWindowData);
          }
          // tslint:disable-next-line:max-line-length
          var programWindowData = this.createWebRTCPreviewViewModel(
            true,
            createRealChannelPreviewResponse.clientId,
            createRealChannelPreviewResponse.brodcasterId,
            createRealChannelPreviewResponse.availabilityZoneUrls,
            true,
            this.debugMode,
            this._currentSelectedChannel.uid,
            this._currentSelectedChannel._availabilityZoneIds,
            "",
            createRealChannelPreviewResponse.tcpOnlyWebRTC,
          );
          this.channelsMap[this._currentSelectedChannel.uid] =
            this._currentSelectedChannel.name;
          if (
            this._channelBroadcastModeChecker.nextLiveBroadcastPresetShow?.id
          ) {
            await this._channelRepository.updateLiveBroadcastShowUid(
              this._currentSelectedChannel.id,
              this._channelBroadcastModeChecker.nextLiveBroadcastPresetShow.id,
            );
          }
          this.programWindowData = programWindowData;
          if (modeUpdated.fromUserStartLive) {
            this.forceUIUpdate();
          }
        }

        if (this._skipToProgramOnStartLiveClicked) {
          this._skipToProgramOnStartLiveClicked = false;
          const relevantItemUids = this._getAllAssetItems()
            .filter((i) => i.isProgram)
            .map((i) => i.uid);
          const uniqueItemUids = [...new Set(relevantItemUids)];
          try {
            await this._socketService.liveBroadcastingHubConnection.invoke(
              "StartLiveBroadcast",
              {
                MainChannelId: this._currentSelectedChannel.id,
                ChannelUid: this.programWindowData.channelUid,
                BroadcastShowId:
                  this._channelBroadcastModeChecker.nextLiveBroadcastPresetShow
                    .id,
                ActionType: ELiveBroadcastingAction.SkipToItem,
                Payload: uniqueItemUids.join(","),
                AvailabilityZoneIds: this.programWindowData.availabilityZoneIds,
              },
            );
          } catch (err) {
            console.error(err);
          }
        }
      } else if (
        !this.creatingProgramWindow &&
        !this.programWindowData &&
        this._currentSelectedChannel
      ) {
        this.creatingProgramWindow = true;
        try {
          const createWebrtcForProgramResult =
            await this._createWebRTCPreviewChannelForBroadcast(
              true,
              this._currentSelectedChannel.id,
            );
          /*console.log(`broadcast.view-model-> start waiting before creating program window pewview`, createWebrtcForProgramResult);
        await (new Promise(resolve => setTimeout(resolve, 10 * 1000)));
        console.log(`broadcast.view-model-> finished waiting before creating program window pewview`, createWebrtcForProgramResult);*/
          if (!modeUpdated.fromUserStartLive) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
          this.programWindowData = this.createWebRTCPreviewViewModel(
            true,
            createWebrtcForProgramResult.clientId,
            createWebrtcForProgramResult.edgeBrodcasterId,
            [createWebrtcForProgramResult.url],
            true,
            this.debugMode,
            createWebrtcForProgramResult.channelUid,
            [createWebrtcForProgramResult.availabilityZoneId],
            "",
          );
          if (modeUpdated.fromUserStartLive) {
            this.forceUIUpdate();
          }
        } catch (err) {
          console.error(err);
        } finally {
          this.creatingProgramWindow = false;
        }
      }
      await this._resubscribeToStackAndChannelBroadcastUpdates();

      this.forceUIUpdate();
      if (this.mode === EChannelBroadcastMode.PreLive) {
        this._sendActionToLiveBroadcastingManager(
          this._currentSelectedChannel.uid,
          ELiveBroadcastingAction.UpdateOperationalChannelCache,
          this.programWindowData.channelUid,
          this.programWindowData.availabilityZoneIds,
        );
      }
    } finally {
      this.duringModeUpdate = false;
    }
  }

  private async _resubscribeToStackAndChannelBroadcastUpdates(): Promise<void> {
    if (this.programWindowData) {
      await this._socketService.startListeningForChannelBroadcastUpdates(
        this.programWindowData.channelUid,
        this.id,
        this._onChannelBroadcastUpdatesEventHandler.bind(this),
      );
      await this._socketService.startListeningForLiveBroadcastStackAssetItemUpdates(
        this.programWindowData.channelUid,
        this.id,
        this._onLiveBroadcastStackAssetItemUpdatedEventHandler.bind(this),
      );
    }
    if (this.previewWindowData) {
      await this._socketService.startListeningForChannelBroadcastUpdates(
        this.previewWindowData.channelUid,
        this.id,
        this._onChannelBroadcastUpdatesEventHandler.bind(this),
      );
    }
  }

  private createWebRTCPreviewViewModel(
    isProgram: boolean,
    clientId: string,
    brodcasterId: string,
    availabilityZoneUrls: Array<string>,
    isMuted: boolean,
    showVideoControls: boolean,
    channelUid: string,
    availabilityZoneIds: number[],
    status: string,
    tcpOnlyWebRTC = false,
  ): WebRTCPreviewViewModel {
    var webrtc = new WebRTCPreviewViewModel(
      isProgram,
      clientId,
      brodcasterId,
      availabilityZoneUrls,
      isMuted,
      showVideoControls,
      channelUid,
      availabilityZoneIds,
      status,
      tcpOnlyWebRTC,
    );
    return webrtc;
  }
  private _onChannelBroadcastUpdatesEventHandler(
    channelUid: string,
    broadcastPresetUid: number,
    assetItemsUids: string[],
    prevPlayedItemsUids: string[],
  ): void {
    // Clear existing timeout for this channelUid
    if (this._channelBroadcastUpdatesDebounceTimeouts[channelUid]) {
      clearTimeout(this._channelBroadcastUpdatesDebounceTimeouts[channelUid]);
    }

    // Set new debounced timeout (300ms delay)
    this._channelBroadcastUpdatesDebounceTimeouts[channelUid] = setTimeout(
      () => {
        this._executeChannelBroadcastUpdates(
          channelUid,
          broadcastPresetUid,
          assetItemsUids,
          prevPlayedItemsUids,
        );
        // Clear the timeout reference after execution
        delete this._channelBroadcastUpdatesDebounceTimeouts[channelUid];
      },
      300,
    );
  }

  private _executeChannelBroadcastUpdates(
    channelUid: string,
    broadcastPresetUid: number,
    assetItemsUids: string[],
    prevPlayedItemsUids: string[],
  ): void {
    const isProgramChannel = this.programWindowData.channelUid === channelUid;
    console.log(
      `broadcast-preset.vm->_executeChannelBroadcastUpdates->channelUid: ${isProgramChannel ? "program" : "preview"} ${channelUid}`,
      assetItemsUids,
    );

    this._getAllAssetItems().forEach(async (assetItem) => {
      // Special handling for stack-type items
      if (assetItem.type === ESwitcherAssetType.Stack) {
        return;
      }
      if (isProgramChannel) {
        if (assetItem.isProgram) {
          assetItem.isProgram = false;
        }
        if (assetItemsUids && assetItemsUids.includes(assetItem.uid)) {
          assetItem.isProgram = true;
        }
      } else {
        if (assetItem.isPreview) {
          assetItem.isPreview = false;
        }
        if (assetItemsUids && assetItemsUids.includes(assetItem.uid)) {
          assetItem.isPreview = true;
        }
      }
    });

    const newProgramItems = this._getAllAssetItems().filter((i) => i.isProgram);
    const scte35InProgram = newProgramItems.find(
      (i) => i.type === SWITCHER_ASSET_TYPE_SCTE35,
    );
    if (
      scte35InProgram &&
      this.programWindowData &&
      this._currentSelectedChannel &&
      this.programWindowData.channelUid === this._currentSelectedChannel.uid
    ) {
      const durationSeconds = scte35InProgram.durationMs
        ? Math.max(0, Math.round(scte35InProgram.durationMs / 1000))
        : 0;
      if (durationSeconds > 0) {
        this.showScte35Tag(durationSeconds);
      }
    } else {
      this.hideScte35Tag();
      this.clearScte35TagTimer();
    }

    this.forceUIUpdate();
  }

  onStartLiveClicked() {
    this._ui.notify(this._ui.EVENTS.ShowConfirm, {
      title: "Start Live",
      message: `Are you sure that you want to start live on channel ${this._currentSelectedChannel.name}?`,
      buttons: [
        {
          title: "Cancel",
          theme: "brigth",
          callback: () => {},
        },
        {
          title: "OK",
          progressSteps: [
            { message: "Starting broadcast preset", duration: 2000 },
            { message: "Setting up channel preview", duration: 3000 },
            { message: "Finalizing broadcast control panel", duration: 0 },
          ],
          callback: async () => {
            const state = this._channelBroadcastModeChecker.getStateForChannel(
              this._currentSelectedChannel.id,
            );
            this._skipToProgramOnStartLiveClicked = true;
            this.mode = EChannelBroadcastMode.Live;
            this.editMode = false;
            this.allowLiveMode = true;
            this.forceUIUpdate();

            try {
              await this._channelRepository.updateLiveBroadcastShowUid(
                this._currentSelectedChannel.id,
                this._channelBroadcastModeChecker.nextLiveBroadcastPresetShow
                  .id,
              );
              await this._channelLiveBroadcastingModeChanged({
                channelId: this._currentSelectedChannel.id,
                channelName: this._currentSelectedChannel.name,
                mode: EChannelBroadcastMode.Live,
                nextShow: state?.nextLiveBroadcastPresetShow,
                broadcastPresetUid: state?.liveBroadcastPresetShowUid,
                fromUserStartLive: true,
              } as BroadcastModeUpdate);
              this.forceUIUpdate();
              const dateTimeUtcNow = moment().utc();
              const checkPeriodEndTime = dateTimeUtcNow
                .clone()
                .add(
                  this._userService.getCurrentUser()
                    .liveBroadcastingCheckPeriodMs,
                );
              this._channelBroadcastModeChecker.triggerModeUpdate(
                dateTimeUtcNow,
                checkPeriodEndTime,
              );
            } catch (err) {
              this.mode = state?.mode ?? EChannelBroadcastMode.PreLive;
              this.editMode = true;
              this.allowLiveMode =
                this._router.url.startsWith("/broadcasts/live/");
              this._skipToProgramOnStartLiveClicked = false;
              this.forceUIUpdate();
              console.error(
                "broadcast-preset.vm->onStartLiveClicked failed:",
                err,
              );
            } finally {
              if (this._startLiveRefreshTimeoutId != null) {
                clearTimeout(this._startLiveRefreshTimeoutId);
              }
              this._startLiveRefreshTimeoutId = setTimeout(() => {
                this._startLiveRefreshTimeoutId = null;
                this._refreshCalendarService();
              }, 5000);
            }
          },
        },
      ],
    });
  }

  async onStopLiveClicked() {
    if (!this.programWindowData) {
      return;
    }
    const availabilityZoneIds = this.programWindowData.availabilityZoneIds;
    this.clearExistingProgramWindowInterval();
    this.allowLiveMode = false;
    this.editMode = true;
    this.programWindowData = null;
    this.forceUIUpdate();

    try {
      // IR-7737: Await so controller clears live state before we update API and run triggerModeUpdate.
      // Otherwise (e.g. after a show extended once) the broadcaster can stay in live mode.
      await this._sendActionsToLiveBroadcastingManager([
        new SendLiveBroadcastingActionRequest(
          this._currentSelectedChannel.uid,
          ELiveBroadcastingAction.InvalidateControllerCache,
          "",
          availabilityZoneIds,
        ),
        new SendLiveBroadcastingActionRequest(
          this._currentSelectedChannel.uid,
          ELiveBroadcastingAction.UpdateLiveBroadcastShowUid,
          "",
          availabilityZoneIds,
        ),
      ]);
      await this._channelRepository.updateLiveBroadcastShowUid(
        this._currentSelectedChannel.id,
        null,
      );

      await this._refreshCalendarService();

      const dateTimeUtcNow = moment().utc();
      const checkPeriodEndTime = dateTimeUtcNow
        .clone()
        .add(this._userService.getCurrentUser().liveBroadcastingCheckPeriodMs);
      const nextShow = this._calendarService.nextShows[0];
      console.log(
        `broadcast-preset.vm->onStopLiveClicked->nextShow: ${nextShow?.name}`,
      );

      if (nextShow) {
        await this._calendarService.skipCurrentShow(
          nextShow.id,
          `live_broadcast_user_id_${this._userService.getCurrentUser().userId}`,
        );
      } else {
        const currentShow = this._calendarService.currentShow;
        if (currentShow) {
          const newDurationMs = dateTimeUtcNow.diff(
            currentShow.startTime,
            "milliseconds",
          );
          console.log(
            `broadcast-preset.vm->onStopLiveClicked->nextShow is null. Updating the duration of the current show: ${currentShow.name}. New Dur: ${newDurationMs}`,
          );
          await this._showRepository.updateShowDuration(
            currentShow.id,
            newDurationMs,
          );
        }
      }
      await this._channelBroadcastModeChecker.triggerModeUpdate(
        dateTimeUtcNow,
        checkPeriodEndTime,
      );
      console.log(`broadcast-preset.vm->onStopLiveClicked->triggerModeUpdate`);
    } catch (err) {
      console.error("broadcast-preset.vm->onStopLiveClicked failed:", err);
    } finally {
      this.forceUIUpdate();
    }
  }

  async onCutClicked() {
    var programSkipActionType = ELiveBroadcastingAction.SkipToItem;
    const relevantProgramItemsUids = [];
    var activeStack = [];

    this._getAllAssetItems()
      .filter((i) => i.isProgram)
      .map((i) => {
        if (i.type == ESwitcherAssetType.Stack) {
          activeStack.push(i);
        } else {
          relevantProgramItemsUids.push(i.uid);
        }
      });

    // let skipToPreviewPromise = Promise.resolve();
    let uniqueItemUidsForPreview = [];
    if (this.previewWindowData) {
      uniqueItemUidsForPreview = [...new Set(relevantProgramItemsUids)];
      // skipToPreviewPromise = this.executeControllerAction(this.previewWindowData, ELiveBroadcastingAction.SkipToItem, uniqueItemUidsForPreview.join(','));
    }

    const relevantPreviewItemsUids = [];
    relevantPreviewItemsUids.push(
      ...this._getAllAssetItems()
        .filter((i) => i.isPreview && i.type !== ESwitcherAssetType.Stack)
        .map((i) => i.uid),
    );

    // let skipToProgramPromise = Promise.resolve();
    let uniqueItemUidsForProgram = [];
    if (this.programWindowData) {
      uniqueItemUidsForProgram = [...new Set(relevantPreviewItemsUids)];
      // skipToProgramPromise = this.executeControllerAction(this.programWindowData, ELiveBroadcastingAction.SkipToItem, uniqueItemUidsForProgram.join(','));
    }
    // tslint:disable-next-line:max-line-length
    // _sendActionToLiveBroadcastingManager
    // tslint:disable-next-line:max-line-length
    // await Promise.all([this.executeControllerAction(this.programWindowData, ELiveBroadcastingAction.SkipToItem, uniqueItemUidsForProgram.join(',')), this.executeControllerAction(this.previewWindowData, ELiveBroadcastingAction.SkipToItem, uniqueItemUidsForPreview.join(','))]);
    // now set flip between program and preview items
    this._getAllAssetItems().forEach((item) => {
      if (item.type === ESwitcherAssetType.Stack) {
        return;
      }
      item.isProgram = item.isPreview = false;

      if (relevantPreviewItemsUids.includes(item.uid)) {
        item.isProgram = true;
      }
      if (relevantProgramItemsUids.includes(item.uid)) {
        item.isPreview = true;
      }
    });

    if (activeStack.length > 0) {
      programSkipActionType =
        ELiveBroadcastingAction.SkipToItemAndSkipFromStack;
      activeStack.map((s) => {
        uniqueItemUidsForProgram.push(`STACK_${s.uid}`);
      });
    }
    await this._sendActionsToLiveBroadcastingManager([
      new SendLiveBroadcastingActionRequest(
        this.previewWindowData.channelUid,
        ELiveBroadcastingAction.SkipToItem,
        uniqueItemUidsForPreview.join(","),
        this.previewWindowData.availabilityZoneIds,
      ),
      new SendLiveBroadcastingActionRequest(
        this.programWindowData.channelUid,
        programSkipActionType,
        uniqueItemUidsForProgram.join(","),
        this.programWindowData.availabilityZoneIds,
      ),
    ]);

    await this.updateBroadcastPreset();
    if (
      programSkipActionType ===
      ELiveBroadcastingAction.SkipToItemAndSkipFromStack
    ) {
      activeStack.forEach((stackItem) => {
        this._onLiveBroadcastStackAssetItemUpdatedEventHandler(
          this.programWindowData.channelUid,
          this.id,
          stackItem.uid.toString(),
          true,
        );
      });
    }
    this.forceUIUpdate();
  }

  adjustAspectRatio(element: HTMLElement, aspectRation: number) {
    console.log("broadcast-preset.vm->onWindowResize", element);
    element.style.height = element.clientWidth / aspectRation + "px";
  }

  async onDebugWindowClicked() {
    console.log(
      "broadcast-preset.vm->onDebugWincowClicked",
      this._calendarService.currentShow,
      this._calendarService.nextShows,
    );
    await this._refreshCalendarService();
  }

  private async _refreshCalendarService(): Promise<void> {
    const dateTimeUtcNow = moment().utc();
    const checkPeriodEndTime = dateTimeUtcNow.clone().add(2, "days");
    await this._calendarService.getByDates(
      this._currentSelectedChannel.id,
      dateTimeUtcNow,
      checkPeriodEndTime,
      false,
      null,
      false,
    );
    this._calendarService.clearCache(true);
    await this._calendarService.updateShows();
  }

  private _asBaseClass(): BroadcastPreset {
    return new BroadcastPreset(
      this.id,
      this.name,
      this.thumbnailUrl,
      this.switchers.map(
        (p) =>
          new Switcher(
            p.name,
            p.items.map(
              (i) =>
                new SwitcherItem(
                  i.uid,
                  i.type,
                  i.index,
                  i.assetName,
                  i.previewUrl,
                  i.assetId,
                  i.somMs,
                  i.durationMs,
                  i.volumeEnabled,
                  i.isProgram,
                  i.isPreview,
                  i.data,
                  i.colSpan ?? 1,
                  i.rowSpan ?? 1,
                ),
            ),
          ),
      ),
      this.audioChannels.map((ac) => ac.clone()),
    );
  }

  onSchedulerClickedEventHandler() {
    this.debugMode = !this.debugMode;

    if (this.programWindowData) {
      this.programWindowData.showVideoControls = this.debugMode;
    }
    if (this.previewWindowData) {
      this.previewWindowData.showVideoControls = this.debugMode;
    }
  }

  /**
   * If the current item in the stack is marked as not visible, advance the stack index in the UI only
   * (no server call). This aligns the UI with instructions that will later advance the stack from the server.
   */
  private _advanceStackIndexIfCurrentItemNotVisible(
    switcherItem: SwitcherItemViewModel,
  ): void {
    const blockData = switcherItem.data as assetBlockItemExtendedData;
    if (!blockData?.assets?.length) {
      return;
    }
    const assetData = blockData.getBroadcastAssetItem(switcherItem.uid);
    const orderedAssets = [...blockData.assets];
    if (
      assetData.assetsOrder &&
      Object.keys(assetData.assetsOrder).length > 0
    ) {
      orderedAssets.sort(
        (a, b) =>
          (assetData.assetsOrder[a.id] ?? 0) -
          (assetData.assetsOrder[b.id] ?? 0),
      );
    }
    while (assetData.stackIndex < orderedAssets.length) {
      const currentId = orderedAssets[assetData.stackIndex].id;
      const isVisible = assetData.assetsVisibility[currentId] ?? true;
      if (isVisible) {
        break;
      }
      assetData.stackIndex++;
    }
    if (assetData.stackIndex >= orderedAssets.length) {
      assetData.stackIndex = Math.max(0, orderedAssets.length - 1);
    }
    switcherItem.dataChanged$.next();
  }

  private _onLiveBroadcastStackAssetItemUpdatedEventHandler(
    channelUid: string,
    broadcastPresetUid: string,
    nowPlayingStackItem: string,
    stackEnded: boolean,
  ) {
    if (this.id !== broadcastPresetUid) {
      return;
    }

    this._getAllAssetItems().forEach(async (assetItem) => {
      // Special handling for stack-type items
      if (nowPlayingStackItem === assetItem.uid) {
        var data = await this._broadcastsRepository.getBlockExtendedData(
          this.id,
          assetItem,
        );
        if (data && data.assets) {
          var assetData = data.getBroadcastAssetItem(
            assetItem.uid,
          ) as broadcastAssetItemExtendedData;
          console.log(
            `broadcast-preset.view-model->_onLiveBroadcastStackAssetItemUpdatedEventHandler-> stackIndex: ${assetData.stackIndex} stackLength: ${assetData.stackLength} stackEnded: ${stackEnded}`,
          );
          if (stackEnded) {
            assetItem.isProgram = false;
            this.channelMediaListChanged.next(true);

            await this._broadcastsRepository.updateBlockExtendedData(
              this.id,
              assetItem.assetId,
              assetItem.uid,
              data,
              false,
            );
            await this.updateBroadcastPreset();
          }
          assetItem.data = data;
          var switcher = this.switchers.find((switcher) =>
            switcher.items.some((item) => item.uid === assetItem.uid),
          );
          if (switcher) {
            (switcher as SwitcherViewModel).updateItems([assetItem]);
            this.forceUIUpdate();
          }
        }
      }
    });
  }

  private async _validateItemOnDrop(
    switcherItem: SwitcherItemViewModel,
  ): Promise<boolean> {
    const currentAssetIdsPerTypeCount =
      await this._generateAssetIdsCountDictionary(
        this._getAllAssetItems().filter(
          (i) => i.type !== ESwitcherAssetType.Empty,
        ),
      );
    const newAssetIdsPerTypeCount = await this._generateAssetIdsCountDictionary(
      [switcherItem],
    );
    const [checkAssetIdLimitsSuccess, assetTypeLimitName, limitCount] =
      this._checkAssetIdLimits(
        currentAssetIdsPerTypeCount,
        newAssetIdsPerTypeCount,
      );
    if (checkAssetIdLimitsSuccess === false) {
      this._ui.notify(this._ui.EVENTS.ShowAlert, {
        title: "Drop Item To Panel Failed",
        message: `You have exceed the limit of ${limitCount} asset Ids for ${assetTypeLimitName} type!`,
      });

      return false;
    }
    const assetsPerTypeCount = this._generateAssetsPerTypeCountDictionary();
    if (
      this._allowedAssetsPerTypeLimit.has(switcherItem.type) &&
      assetsPerTypeCount.get(switcherItem.type) + 1 >
        this._allowedAssetsPerTypeLimit.get(switcherItem.type)
    ) {
      this._ui.notify(this._ui.EVENTS.ShowAlert, {
        title: "Drop Item To Panel Failed",
        message: `You have exceed the limit of ${this._allowedAssetsPerTypeLimit.get(switcherItem.type)} items for ${ESwitcherAssetType[switcherItem.type].toString()} type!`,
      });

      return false;
    }

    return true;
  }
  private _checkAssetIdLimits(
    currentAssetIdCounts: Map<ESwitcherAssetType, Map<string, number>>,
    newAssetIdCounts: Map<ESwitcherAssetType, Map<string, number>>,
  ): [boolean, string, number] {
    for (const newAssetType of newAssetIdCounts.keys()) {
      console.log(
        `broadcast-preset.view-model->_checkAssetIdLimits-> checking type ${ESwitcherAssetType[newAssetType].toString()}`,
      );
      for (const newAssetId of newAssetIdCounts.get(newAssetType).keys()) {
        console.log(
          `broadcast-preset.view-model->_checkAssetIdLimits-> checking type: ${ESwitcherAssetType[newAssetType].toString()} ,newAssetId ${newAssetId}`,
        );
        if (
          currentAssetIdCounts.has(newAssetType) &&
          currentAssetIdCounts.get(newAssetType).has(newAssetId) &&
          currentAssetIdCounts.get(newAssetType).get(newAssetId) +
            newAssetIdCounts.get(newAssetType).get(newAssetId) >
            this._allowedAssetIdPerTypeLimit.get(newAssetType)
        ) {
          return [
            false,
            ESwitcherAssetType[newAssetType].toString(),
            this._allowedAssetIdPerTypeLimit.get(newAssetType),
          ];
        }
      }
    }
    return [true, "", 0];
  }
  private async _generateAssetIdsCountDictionary(
    switchItems: SwitcherItem[],
  ): Promise<Map<ESwitcherAssetType, Map<string, number>>> {
    const result = new Map<ESwitcherAssetType, Map<string, number>>();

    const allAssetIds = [];
    for (const asset of switchItems) {
      allAssetIds.push({ assetId: asset.assetId, type: asset.type });

      if (asset.type === ESwitcherAssetType.Block) {
        const blockItems = await this._showRepository.getByDates(
          asset.assetId as number,
          moment().utc().subtract(100, "years"),
          moment().utc().add(100, "years"),
          true,
        );
        for (const show of blockItems) {
          /*for (const secEvent of show.secEvents) {
              if (secEvent.type === SecEventType.FloatingMedia) {
                allAssetIds.push({ assetId : secEvent., type: ESwitcherAssetType.Media });
              } else  if (secEvent.type === SecEventType.Graphics) {
                allAssetIds.push({ assetId : secEvent.id, type: ESwitcherAssetType.Gfx });
              }

            }*/

          if (show instanceof LayoutShow) {
            const layout = (
              await this._layoutRepository.getLayouts(
                null,
                (show as LayoutShow).layoutUid,
              )
            )[0];
            if (layout) {
              allAssetIds.push({
                assetId: layout.id,
                type: ESwitcherAssetType.Layout,
              });
            }
            for (const layoutZone of layout.zones.filter(
              (z) => z.zoneType !== ELayoutZoneType.empty,
            )) {
              allAssetIds.push(layoutZone.mediaId.toString());
            }
          } else if (show instanceof MediaShow) {
            allAssetIds.push({
              assetId: show.mediaId,
              type: ESwitcherAssetType.Media,
            });
          } else if (show instanceof LiveShow) {
            allAssetIds.push({
              assetId: show.feedId,
              type: ESwitcherAssetType.LiveFeed,
            });
          }
        }
      } else if (asset.type === ESwitcherAssetType.Layout) {
        const layout = (
          await this._layoutRepository.getLayouts(
            null,
            asset.assetId.toString(),
          )
        )[0];
        for (const layoutZone of layout.zones.filter(
          (z) => z.zoneType !== ELayoutZoneType.empty,
        )) {
          if (layoutZone.zoneType === ELayoutZoneType.liveFeed) {
            allAssetIds.push({
              assetId: layoutZone.mediaId,
              type: ESwitcherAssetType.LiveFeed,
            });
          } else if (layoutZone.zoneType === ELayoutZoneType.gfx) {
            allAssetIds.push({
              assetId: layoutZone.mediaId,
              type: ESwitcherAssetType.Gfx,
            });
          } else if (layoutZone.zoneType === ELayoutZoneType.media) {
            allAssetIds.push({
              assetId: layoutZone.mediaId,
              type: ESwitcherAssetType.Media,
            });
          }
        }
      }
    }

    for (const asset of allAssetIds) {
      if (result.has(asset.type) === false) {
        const assetDic = new Map<string, number>();
        assetDic.set(asset.assetId.toString(), 1);
        result.set(asset.type, assetDic);
      } else {
        if (result.get(asset.type).has(asset.assetId.toString()) === false) {
          result.get(asset.type).set(asset.assetId.toString(), 1);
        } else {
          result
            .get(asset.type)
            .set(
              asset.assetId.toString(),
              result.get(asset.type).get(asset.assetId.toString()) + 1,
            );
        }
      }
    }

    return result;
  }

  private _generateAssetsPerTypeCountDictionary(): Map<
    ESwitcherAssetType,
    number
  > {
    const result = new Map<ESwitcherAssetType, number>();
    const allAssets = this._getAllAssetItems().filter(
      (i) => i.type !== ESwitcherAssetType.Empty,
    );

    for (const asset of allAssets) {
      if (result.has(asset.type) === false) {
        result.set(asset.type, 1);
      } else {
        result.set(asset.type, result.get(asset.type) + 1);
      }
    }

    return result;
  }
  public handleContentEditablePaste(event: ClipboardEvent, property: string) {
    event.preventDefault();
    const text = event.clipboardData.getData("text/plain");
    if (text) {
      this[property] = this[property] + text.replace(/[\r\n]+/g, " ");
    }
  }

  onVideoConnectionStateChanged(
    previewWindowData: WebRTCPreviewViewModel,
    newState: string,
  ) {
    console.log(
      `broadcast-preset.view-model->onVideoConnectionStateChanged-> isProgram=${previewWindowData.isProgram}-> newState=${newState}`,
    );
    switch (newState) {
      case "connected":
        // The connection has become fully connected
        break;
      case "disconnected":
      case "failed":
      case "signalling-failed":
        if (previewWindowData.isProgram) {
          this.programWindowData = null;
          this.forceUIUpdate();

          const programReconnectId = setTimeout(() => {
            this._videoReconnectTimeoutIds =
              this._videoReconnectTimeoutIds.filter(
                (id) => id !== programReconnectId,
              );
            console.log(
              `broadcast-preset.view-model->onVideoConnectionStateChanged-> isProgram=${previewWindowData.isProgram}-> reconnect`,
            );

            this.programWindowData = previewWindowData;
            this.forceUIUpdate();
          }, 2000);
          this._videoReconnectTimeoutIds.push(programReconnectId);
        } else {
          this.previewWindowData = null;
          this.forceUIUpdate();

          const previewReconnectId = setTimeout(() => {
            this._videoReconnectTimeoutIds =
              this._videoReconnectTimeoutIds.filter(
                (id) => id !== previewReconnectId,
              );
            console.log(
              `broadcast-preset.view-model->onVideoConnectionStateChanged-> isProgram=${previewWindowData.isProgram}-> reconnect`,
            );

            this.previewWindowData = previewWindowData;
            this.forceUIUpdate();
          }, 2000);
          this._videoReconnectTimeoutIds.push(previewReconnectId);
        }
        // One or more transports has terminated unexpectedly or in an error
        break;
      case "closed":
        // The connection has been closed
        break;
    }
  }

  toggleCutButton() {
    this.forceShowingCutButton = !this.forceShowingCutButton;
  }
  public clearExistingProgramWindowInterval(
    channelUid: string = this.programWindowData?.channelUid,
  ) {
    const existingprogramWindowInterval =
      this._intervalsPerChannelUid[channelUid];
    if (existingprogramWindowInterval) {
      clearInterval(existingprogramWindowInterval);
      console.log(
        `broadcast-preset.vm->_channelLiveBroadcastingModeChanged-> clearing interval for program channel ${channelUid}`,
      );
    }
  }
  public async sendUpdateStackWarmup(blockId: number, assetItemUid: string) {
    const payload = JSON.stringify({ blockId, assetItemUid });
    // Always update program first so the on-air engine is consistent before preview catches up.
    if (this.programWindowData) {
      await this.executeControllerAction(
        this.programWindowData,
        ELiveBroadcastingAction.UpdateStackWarmup,
        payload,
      );
    }
    if (this.previewWindowData) {
      await this.executeControllerAction(
        this.previewWindowData,
        ELiveBroadcastingAction.UpdateStackWarmup,
        payload,
      );
    }
  }

  private async onSwitchItemBlockTypeChanged(switcherItem: SwitcherItem) {
    if (this.programWindowData) {
      await this.executeControllerAction(
        this.programWindowData,
        ELiveBroadcastingAction.RemoveAssetItemsFromPrerollList,
        switcherItem.uid,
      );
      await this.executeControllerAction(
        this.programWindowData,
        ELiveBroadcastingAction.AddAssetItemsToPrerollList,
        switcherItem.uid,
      );
    }
    if (this.previewWindowData) {
      await this.executeControllerAction(
        this.previewWindowData,
        ELiveBroadcastingAction.RemoveAssetItemsFromPrerollList,
        switcherItem.uid,
      );
      await this.executeControllerAction(
        this.previewWindowData,
        ELiveBroadcastingAction.AddAssetItemsToPrerollList,
        switcherItem.uid,
      );
    }
  }
}

export enum EKeymapShortcutsPerMedia {
  Q,
  W,
  E,
  R,
  T,
  Y,
  A,
  S,
  D,
  F,
  G,
  H,
}

/** Server EMediaType.StreamInAudio (value 16) - used to detect live feed audio items in playlist */
const STREAM_IN_AUDIO_MEDIA_TYPE = 16;

interface PlaylistMediaItemDto {
  ItemUID: string;
  ItemName?: string;
  MediaType?: number | string;
  StartTime?: string;
  DurationMS?: number;
}

interface PlaylistDto {
  MediaList?: Record<string, PlaylistMediaItemDto>;
  PrerolledMedia?: Record<string, PlaylistMediaItemDto>;
}

class CreateBroadcastPreviewResult {
  clientId: string;
  edgeBrodcasterId: string;
  url: string;
  channelUid: string;
  availabilityZoneId: number;
}

class ExtendPreviewChannelExpirationDateResult {
  result: string;
}

class SendLiveBroadcastingActionResult {
  result: string;
  resultCode: number;
}

class SendLiveBroadcastingActionRequest {
  constructor(
    public channelUid: string,
    public actionType: ELiveBroadcastingAction,
    public payload: string,
    public availabilityZoneIds: number[],
  ) {}
}

class WebRTCPreviewViewModel extends WebRTCPreview {
  /** Set to true after the video has fired (playing) once. Used to avoid unmuting before first play() (browser autoplay policy). */
  public previewVideoHasStartedPlaying = false;

  constructor(
    public isProgram: boolean,
    public clientId: string,
    public brodcasterId: string,
    public availabilityZoneUrls: Array<string>,
    public isMuted: boolean,
    public showVideoControls: boolean,
    public channelUid: string,
    public availabilityZoneIds: number[],
    public status: string,
    public tcpOnlyWebRTC: boolean = false,
  ) {
    super(clientId, brodcasterId, availabilityZoneUrls, tcpOnlyWebRTC);
  }

  public clearStatus() {
    this.status = null;
  }
  public setStatusText(actionType: ELiveBroadcastingAction) {
    switch (actionType) {
      case ELiveBroadcastingAction.AddAssetItemsToPrerollList:
        this.status = `Adding a new item...`;
        break;
      case ELiveBroadcastingAction.SkipToItem:
        this.status = `Switching to a new item...`;
        break;
      case ELiveBroadcastingAction.UpdateEngineChannelFullPlaylist:
      case ELiveBroadcastingAction.UpdateEngineChannelPlaylistWithoutPreroll:
      case ELiveBroadcastingAction.InvalidateControllerCache:
      case ELiveBroadcastingAction.GetControllerCache:
      case ELiveBroadcastingAction.CreateChannelCacheWithFullPlaylist:
        this.status = `Processing...`;
        break;
    }
  }
}
